import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useMemo } from 'react';
import { FaChartBar, FaDownload, FaFilter, FaSync, FaFilePdf, FaFileExcel, FaFileCsv, FaTable, FaTimes, FaCheck, FaPlus, FaMinus } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { ChartDisplay } from './charts';
import './css/ReportDataViewer.css';
import { formatCurrency, formatNumber, formatPercent, formatDate, formatDateTime } from '../../utils/formatUtils';
import {
  buildExchangeRateLookupFromCurrencyList,
  calculateReportAggregation,
  formatAggregatedDisplayForReport,
  formatReportCurrencyCell,
  getAggregationNumericResult,
  getAggregatableNumericForExport,
} from '../../utils/reportCurrencyAggregate';
import jwt_decode from 'jwt-decode';
import CurrencyApi from '../../api/CurrencyApi';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';

// Helper function to parse display config
const parseDisplayConfig = (displayConfig) => {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch (e) {
      return {};
    }
  }
  return displayConfig || {};
};

// Resolve currency with priority: token currency (user currency or company currency) > default
const resolveCurrency = (fallbackCurrency = 'INR') => {
  // Check currency from JWT token
  // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
  // So it will be user.currency if available, otherwise company.currency
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwt_decode(token);
      // Currency in token is: user.currency if available, otherwise company.currency
      const tokenCurrency = decoded.currency;
      if (tokenCurrency) {
        return tokenCurrency;
      }
    }
  } catch (error) {
    console.error('Error decoding token for currency:', error);
  }

  // Fallback to default
  return fallbackCurrency;
};

// Helper function to format values based on data type and enriched data structure
const formatValue = (fieldData, dataType, displayConfig = null) => {
  // Handle enriched data structure (from backend with display values)
  if (typeof fieldData === 'object' && fieldData !== null) {
    // If it's a lookup field with display value, show the display value
    if (fieldData.isReference && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    // If it's a picklist field with display value, show the display value
    if ((fieldData.isPicklist || fieldData.isMultiPicklist) && fieldData.displayValue) {
      return fieldData.displayValue;
    }
    // Otherwise, use the value property
    const value = fieldData.value;
    if (value === null || value === undefined || value === '') return 'N/A';

    // Handle rollup summary with display_config
    if (dataType === 'rollupsummary' && displayConfig) {
      const parsedConfig = parseDisplayConfig(displayConfig);
      const rollupConfig = parsedConfig.rollupSummary || {};
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();

      if (outputType === 'currency') {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(numValue, currencyCode);
      } else if (outputType === 'date') {
        return formatDate(value);
      } else if (outputType === 'datetime') {
        return formatDateTime(value);
      } else {
        // Default to number
        const numVal = parseFloat(value);
        if (isNaN(numVal)) return 'N/A';
        return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }

    switch (dataType) {
      case 'currency':
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 'N/A';
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(numValue, currencyCode);
      case 'number':
      case 'decimal':
        const numVal = parseFloat(value);
        if (isNaN(numVal)) return 'N/A';
        return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      case 'percent':
        const percentVal = parseFloat(value);
        if (isNaN(percentVal)) return 'N/A';
        return formatPercent(percentVal);
      case 'date':
        return formatDate(value);
      case 'datetime':
        return formatDateTime(value);
      default:
        return value.toString();
    }
  }

  // Handle simple value (fallback for backward compatibility)
  const value = fieldData;
  if (value === null || value === undefined || value === '') return 'N/A';

  // Handle rollup summary with display_config
  if (dataType === 'rollupsummary' && displayConfig) {
    const parsedConfig = parseDisplayConfig(displayConfig);
    const rollupConfig = parsedConfig.rollupSummary || {};
    const outputType = (rollupConfig.outputType || 'number').toLowerCase();

    if (outputType === 'currency') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return 'N/A';
      return formatCurrency(numValue, 'INR');
    } else if (outputType === 'date') {
      return formatDate(value);
    } else if (outputType === 'datetime') {
      return formatDateTime(value);
    } else {
      // Default to number
      const numVal = parseFloat(value);
      if (isNaN(numVal)) return 'N/A';
      return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

  switch (dataType) {
    case 'currency':
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return 'N/A';
      return formatCurrency(numValue, 'INR');
    case 'number':
    case 'decimal':
    case 'rollupsummary':
      const numVal = parseFloat(value);
      if (isNaN(numVal)) return 'N/A';
      return formatNumber(numVal, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case 'percent':
      const percentVal = parseFloat(value);
      if (isNaN(percentVal)) return 'N/A';
      return formatPercent(percentVal);
    case 'date':
      return formatDate(value);
    case 'datetime':
      return formatDateTime(value);
    default:
      return value.toString();
  }
};

function makeReportFormatAggregatedDisplay(baseCurrencyCode) {
  return (result, field, aggregation) =>
    formatAggregatedDisplayForReport(
      result,
      field,
      aggregation,
      baseCurrencyCode,
      (res, fld) =>
        formatValue(res, fld.data_type, fld.display_config || fld.displayConfig)
    );
}

/** Client-only report viewer sort (does not call API or change saved report). */
function getViewerRuntimeSortComparable(field, row) {
  if (!field) return null;
  const tableName = field.object_name || 'main';
  const aliased = `${tableName}_${field.field_name}`;
  const raw = row[aliased] || row[field.field_name];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const v = raw.value;
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = parseFloat(v);
    if (
      !Number.isNaN(n) &&
      String(v).trim() !== '' &&
      Number.isFinite(n)
    ) {
      return n;
    }
    return String(v).toLowerCase();
  }
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  const n = parseFloat(raw);
  if (!Number.isNaN(n) && String(raw).trim() !== '') return n;
  return String(raw).toLowerCase();
}

function compareViewerRuntimeSortValues(a, b) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function deepCloneNestedReportGroups(obj) {
  if (Array.isArray(obj)) {
    return obj.slice();
  }
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const k of Object.keys(obj)) {
      out[k] = deepCloneNestedReportGroups(obj[k]);
    }
    return out;
  }
  return obj;
}

function sortReportNestedLeaves(node, field, direction) {
  if (Array.isArray(node)) {
    const dirMult = direction === 'desc' ? -1 : 1;
    node.sort((rowA, rowB) => {
      const va = getViewerRuntimeSortComparable(field, rowA);
      const vb = getViewerRuntimeSortComparable(field, rowB);
      return dirMult * compareViewerRuntimeSortValues(va, vb);
    });
    return;
  }
  if (node && typeof node === 'object') {
    for (const k of Object.keys(node)) {
      sortReportNestedLeaves(node[k], field, direction);
    }
  }
}

// Function to render grouped data
const renderGroupedData = (
  data,
  selectedFields,
  grouping,
  summarizedFields,
  baseCurrencyCode = null,
  exchangeRateLookup = null,
  runtimeSort = null
) => {
  const aggregationFormatter = makeReportFormatAggregatedDisplay(baseCurrencyCode);

  const formatDataCell = (fieldData, field, row) => {
    const money = formatReportCurrencyCell(
      fieldData,
      field,
      row,
      baseCurrencyCode,
      resolveCurrency('INR'),
      exchangeRateLookup
    );
    if (money != null) return money;
    return formatValue(
      fieldData,
      field.data_type,
      field.display_config || field.displayConfig
    );
  };
  // Get all grouped fields
  const groupedFields = grouping.groupedFields;


  // Filter out grouped fields from selectedFields to avoid duplication
  const nonGroupedFields = selectedFields.filter(field =>
    !grouping.groupedFields.some(groupField =>
      groupField.field_name === field.field_name &&
      groupField.object_name === field.object_name
    )
  );

  // Helper functions to get grouping key and display label from row
  const getGroupKey = (field, row) => {
    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;
    const fieldData = row[aliasedFieldName] || row[field.field_name];

    if (fieldData) {
      if (typeof fieldData === 'object' && fieldData !== null) {
        if (fieldData.value !== undefined && fieldData.value !== null && fieldData.value !== '') {
          return String(fieldData.value);
        }
        if (fieldData.displayValue !== undefined && fieldData.displayValue !== null && fieldData.displayValue !== '') {
          return String(fieldData.displayValue);
        }
        return 'N/A';
      }
      return String(fieldData);
    }
    return 'N/A';
  };

  const getGroupLabel = (field, row) => {
    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;
    const fieldData = row[aliasedFieldName] || row[field.field_name];

    if (fieldData) {
      if (typeof fieldData === 'object' && fieldData !== null) {
        if (fieldData.displayValue !== undefined && fieldData.displayValue !== null && fieldData.displayValue !== '') {
          return String(fieldData.displayValue);
        }
        if (fieldData.value !== undefined && fieldData.value !== null && fieldData.value !== '') {
          return String(fieldData.value);
        }
        return 'N/A';
      }
      return String(fieldData);
    }
    return 'N/A';
  };

  // Simple groupByFields function
  function groupByFields(data, groupFields) {
    return data.reduce((acc, row) => {
      let currentLevel = acc;
      groupFields.forEach((field, index) => {
        const fieldValue = getGroupKey(field, row);

        // if value not initialized, create new object/array
        if (!currentLevel[fieldValue]) {
          currentLevel[fieldValue] = (index === groupFields.length - 1) ? [] : {};
        }

        // go deeper
        if (index === groupFields.length - 1) {
          currentLevel[fieldValue].push(row);
        } else {
          currentLevel = currentLevel[fieldValue];
        }
      });

      return acc;
    }, {});
  }

  const nestedGroups = groupByFields(data, groupedFields);

  let groupsForRender = nestedGroups;
  if (
    runtimeSort &&
    runtimeSort.fieldId != null &&
    (runtimeSort.direction === 'asc' || runtimeSort.direction === 'desc')
  ) {
    const sortField =
      selectedFields.find(f => String(f.id) === String(runtimeSort.fieldId)) ||
      groupedFields.find(f => String(f.id) === String(runtimeSort.fieldId));
    if (sortField) {
      groupsForRender = deepCloneNestedReportGroups(nestedGroups);
      sortReportNestedLeaves(groupsForRender, sortField, runtimeSort.direction);
    }
  }

  // Create printedGroups and calculatedGroup objects
  const printedGroups = {};
  const calculatedGroup = {};

  // Initialize all group keys from nestedGroups with hierarchical composite keys
  const initializeGroups = (groups, fieldIndex = 0, parentPath = '') => {
    for (const key in groups) {
      const value = groups[key];
      const field = groupedFields[fieldIndex];
      const currentPath = parentPath ? `${parentPath}|${key}` : key;
      const compositeKey = `${field.field_name}_${currentPath}`;

      if (Array.isArray(value)) {
        printedGroups[compositeKey] = false;
        calculatedGroup[compositeKey] = 0;
      } else if (typeof value === 'object' && value !== null) {
        printedGroups[compositeKey] = false;
        calculatedGroup[compositeKey] = 0;
        initializeGroups(value, fieldIndex + 1, currentPath);
      }
    }
  };

  initializeGroups(nestedGroups);

  // Helper function to find the hierarchical path for a row in nestedGroups
  const findRowPath = (row, groups, fieldIndex = 0, currentPath = '') => {
    const field = groupedFields[fieldIndex];
    const fieldValue = getGroupKey(field, row);

    if (!fieldValue || !groups[fieldValue]) {
      return null;
    }

    const newPath = currentPath ? `${currentPath}|${fieldValue}` : fieldValue;

    if (fieldIndex === groupedFields.length - 1) {
      const rows = groups[fieldValue];
      if (Array.isArray(rows) && rows.includes(row)) {
        return newPath;
      }
    } else {
      return findRowPath(row, groups[fieldValue], fieldIndex + 1, newPath);
    }

    return null;
  };

  // Fill the counts using hierarchical composite keys
  data.forEach((row) => {
    const rowPath = findRowPath(row, nestedGroups);
    if (rowPath) {
      groupedFields.forEach((field, fieldIndex) => {
        const fieldValue = getGroupKey(field, row);
        if (fieldValue) {
          const pathParts = rowPath.split('|');
          const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
          const compositeKey = `${field.field_name}_${currentPath}`;

          if (calculatedGroup.hasOwnProperty(compositeKey)) {
            calculatedGroup[compositeKey]++;
          }
        }
      });
    }
  });
  console.log('## calculatedGroup', calculatedGroup);
  const grandTotal = data.length;

  // If no groups were created, show error message
  if (!nestedGroups || Object.keys(nestedGroups).length === 0) {
    return (
      <div>
        <div style={{ color: 'red', padding: '10px' }}>
          Error: No groups could be created. Check console for details.
        </div>
      </div>
    );
  }

  // Render nested grouped data
  const renderNestedGroups = (groups, level = 0, parentKey = '') => {
    if (!groups || typeof groups !== 'object') {
      return <tr><td colSpan={selectedFields.length}>Error: Invalid groups data</td></tr>;
    }

    const results = [];

    for (const key in groups) {
      const value = groups[key];

      if (Array.isArray(value)) {
        // Last level → render rows
        const groupKey = parentKey ? `${parentKey}|${key}` : key;

        // All group data rows
        value.forEach((row, index) => {
          results.push(
            <tr key={`${groupKey}-${index}`} className="data-row grouped-row">
              {/* Show grouped field values */}
              {groupedFields.map((field, fieldIndex) => {
                const currentKey = getGroupKey(field, row);
                const currentLabel = getGroupLabel(field, row);
                let shouldShowValue = false;
                let displayValue = '';

                // Create a hierarchical composite key for this group value
                const rowPath = findRowPath(row, nestedGroups);
                let compositeKey = `${field.field_name}_${currentKey}`;

                if (rowPath && fieldIndex > 0) {
                  const pathParts = rowPath.split('|');
                  const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
                  compositeKey = `${field.field_name}_${currentPath}`;
                }

                // Show value only if not printed before
                if (printedGroups[compositeKey] === false) {
                  shouldShowValue = true;
                  printedGroups[compositeKey] = true;

                  const count = calculatedGroup[compositeKey] || 0;
                  displayValue = `${currentLabel} (${count})`;
                }

                return (
                  <td
                    key={field.id}
                    className="data-cell grouped-field-cell"
                    style={{
                      textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left'
                    }}
                  >
                    {shouldShowValue ? displayValue : '--'}
                  </td>
                );
              })}
              {/* Data for non-grouped fields */}
              {nonGroupedFields.map(field => {
                const tableName = field.object_name || 'main';
                const aliasedFieldName = `${tableName}_${field.field_name}`;
                const fieldData = row[aliasedFieldName] || row[field.field_name];
                return (
                  <td
                    key={field.id}
                    className="data-cell"
                    style={{
                      textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left'
                    }}
                  >
                    {formatDataCell(fieldData, field, row)}
                  </td>
                );
              })}
            </tr>
          );
        });

        // Subtotal row for this group (at last level)
        // Calculate composite key for this group to get the stored count
        let storedCount = 0;

        // Handle both array (last level) and object (intermediate level) cases
        if (value && (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0)) {
          let currentValue, rowPath;

          if (Array.isArray(value)) {
            // Last level - value is an array of records
            const firstRow = value[0];
            currentValue = getGroupKey(groupedFields[level], firstRow);
            rowPath = findRowPath(firstRow, nestedGroups);
          } else {
            // Intermediate level - value is an object with nested groups
            // Get the key (which is the current group value)
            currentValue = key; // Use the key directly since it represents the group value

            // Find a sample row from the nested data to get the path
            const sampleRow = Object.values(value)[0]?.[0]; // Get first record from first nested group
            if (sampleRow) {
              rowPath = findRowPath(sampleRow, nestedGroups);
            }
          }

          // Create composite key matching the structure in calculatedGroup
          let compositeKey;
          if (level === 0) {
            // First level: field_name + value
            compositeKey = `${groupedFields[level].field_name}_${currentValue}`;
            console.log('## Level 0 compositeKey', compositeKey);
          } else {
            // Subsequent levels: field_name + path
            const pathParts = rowPath.split('|');
            const currentPath = pathParts.slice(0, level + 1).join('|');
            compositeKey = `${groupedFields[level].field_name}_${currentPath}`;
          }
          console.log('## compositeKey', compositeKey);
          storedCount = calculatedGroup[compositeKey] || 0;
          console.log('Subtotal debug:', { level, currentValue, rowPath, compositeKey, storedCount });
        }

        results.push(
          <tr key={`${groupKey}-subtotal`} className="subtotal-row">
            {groupedFields.map((field, fieldIndex) => (
              <td key={field.id} className="subtotal-cell">
                {fieldIndex === level ? <span className="subtotal-label">Subtotal ({storedCount})</span> : ''}
              </td>
            ))}
            {nonGroupedFields.map(field => {
              const summarizedField = summarizedFields[field.id];
              if (summarizedField) {
                const aggregatedValue = calculateReportAggregation(
                  value,
                  field,
                  summarizedField.aggregation,
                  baseCurrencyCode,
                  aggregationFormatter,
                  exchangeRateLookup
                );
                return (
                  <td
                    key={field.id}
                    className="subtotal-data-cell"
                    style={{
                      textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left'
                    }}
                  >
                    {aggregatedValue}
                  </td>
                );
              } else {
                return (
                  <td key={field.id} className="subtotal-empty-cell"></td>
                );
              }
            })}
          </tr>
        );
      } else {
        // Not last level → recursively render sub-groups
        const subResults = renderNestedGroups(value, level + 1, parentKey ? `${parentKey}|${key}` : key);
        results.push(...subResults);

        // Add subtotal for this group level (intermediate level subtotal)
        const subtotalKey = `subtotal_${parentKey}_${key}_${level}`;

        // Calculate total rows for this group level using getAllRows
        const getAllRows = (obj) => {
          if (!obj || typeof obj !== 'object') return [];
          let allRows = [];
          for (const subKey in obj) {
            const subValue = obj[subKey];
            if (Array.isArray(subValue)) {
              allRows = allRows.concat(subValue);
            } else if (typeof subValue === 'object' && subValue !== null) {
              allRows = allRows.concat(getAllRows(subValue));
            }
          }
          return allRows;
        };
        const totalRows = getAllRows(value).length;

        results.push(
          <tr key={subtotalKey} className="subtotal-row">
            {/* Empty cells for all grouped fields except the current level which shows Subtotal */}
            {groupedFields.map((field, fieldIndex) => (
              <td key={field.id} className="subtotal-cell">
                {fieldIndex === level ? <span className="subtotal-label">Subtotal ({totalRows})</span> : ''}
              </td>
            ))}
            {/* Show aggregated values for summarized fields, empty cells for others */}
            {nonGroupedFields.map(field => {
              const summarizedField = summarizedFields[field.id];
              if (summarizedField) {
                // Calculate subtotal for this group - get all rows from nested structure
                const getAllRows = (obj) => {
                  let allRows = [];
                  for (const subKey in obj) {
                    const subValue = obj[subKey];
                    if (Array.isArray(subValue)) {
                      allRows = allRows.concat(subValue);
                    } else if (typeof subValue === 'object' && subValue !== null) {
                      allRows = allRows.concat(getAllRows(subValue));
                    }
                  }
                  return allRows;
                };

                const groupData = getAllRows(value);
                const aggregatedValue = calculateReportAggregation(
                  groupData,
                  field,
                  summarizedField.aggregation,
                  baseCurrencyCode,
                  aggregationFormatter,
                  exchangeRateLookup
                );
                return (
                  <td
                    key={field.id}
                    className="subtotal-data-cell"
                    style={{
                      textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left'
                    }}
                  >
                    {aggregatedValue}
                  </td>
                );
              } else {
                return (
                  <td key={field.id} className="subtotal-empty-cell"></td>
                );
              }
            })}
          </tr>
        );
      }
    }

    return results;
  };

  const groupedRows = renderNestedGroups(groupsForRender);

  // Add grand total row
  const grandTotalRow = (
    <tr key="grand-total" className="grand-total-row">
      {groupedFields.map((field, fieldIndex) => (
        <td key={field.id} className="grand-total-cell">
          {fieldIndex === groupedFields.length - 1 ? <span className="grand-total-label">Grand Total ({grandTotal})</span> : ''}
        </td>
      ))}
      {nonGroupedFields.map(field => {
        const summarizedField = summarizedFields[field.id];
        if (summarizedField) {
          const aggregatedValue = calculateReportAggregation(
            data,
            field,
            summarizedField.aggregation,
            baseCurrencyCode,
            aggregationFormatter,
            exchangeRateLookup
          );
          return (
            <td
              key={field.id}
              className="grand-total-data-cell"
              style={{
                textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left'
              }}
            >
              {aggregatedValue}
            </td>
          );
        } else {
          return (
            <td key={field.id} className="grand-total-empty-cell"></td>
          );
        }
      })}
    </tr>
  );

  groupedRows.push(grandTotalRow);


  return groupedRows;
};

const ReportDataViewer = forwardRef(({ reportId, dataLimit = 100000, showFilters = false, onFilterToggle, onFiltersLoaded, onChartsLoaded }, ref) => {
  const [reportData, setReportData] = useState(null);
  const [reportInfo, setReportInfo] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [grouping, setGrouping] = useState(null);
  const [summarizedFields, setSummarizedFields] = useState({});
  const [error, setError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [reportFilters, setReportFilters] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [charts, setCharts] = useState([]);
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);
  const [showChartsSection, setShowChartsSection] = useState(true);
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);
  /** On-screen sort only; does not persist or refetch. */
  const [runtimeSort, setRuntimeSort] = useState(null);

  const viewerColumnFields = useMemo(() => {
    if (!selectedFields.length) return [];
    if (selectedGroups.length > 0) {
      return [
        ...selectedGroups,
        ...selectedFields.filter(
          field =>
            !selectedGroups.some(
              g =>
                g.field_name === field.field_name &&
                g.object_name === field.object_name
            )
        ),
      ];
    }
    return selectedFields;
  }, [selectedFields, selectedGroups]);

  const viewerFlatRows = useMemo(() => {
    if (selectedGroups.length > 0) return previewData;
    if (
      !runtimeSort?.fieldId ||
      (runtimeSort.direction !== 'asc' && runtimeSort.direction !== 'desc')
    ) {
      return previewData;
    }
    const field = selectedFields.find(
      f => String(f.id) === String(runtimeSort.fieldId)
    );
    if (!field || !Array.isArray(previewData) || previewData.length === 0) {
      return previewData;
    }
    const sorted = [...previewData];
    const dirMult = runtimeSort.direction === 'desc' ? -1 : 1;
    sorted.sort((a, b) => {
      const va = getViewerRuntimeSortComparable(field, a);
      const vb = getViewerRuntimeSortComparable(field, b);
      return dirMult * compareViewerRuntimeSortValues(va, vb);
    });
    return sorted;
  }, [previewData, runtimeSort, selectedFields, selectedGroups]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    refreshData: () => {
      loadReportData();
    },
    exportData: (format) => {
      // Call the appropriate export function based on format
      switch (format) {
        case 'pdf':
          if (typeof exportToPDF === 'function') {
            exportToPDF();
          } else {
            console.error('exportToPDF function not available yet');
          }
          break;
        case 'pdf-with-charts':
          if (typeof exportToPDFWithCharts === 'function') {
            exportToPDFWithCharts();
          } else {
            console.error('exportToPDFWithCharts function not available yet');
          }
          break;
        case 'excel':
          if (typeof exportToExcel === 'function') {
            exportToExcel();
          } else {
            console.error('exportToExcel function not available yet');
          }
          break;
        case 'formatted-excel':
          if (typeof exportToFormattedExcel === 'function') {
            exportToFormattedExcel();
          } else {
            console.error('exportToFormattedExcel function not available yet');
          }
          break;
        default:
          console.error(`Unknown export format: ${format}`);
      }
    },
    getFilterCount: () => {
      return reportFilters ? reportFilters.length : 0;
    }
  }));


  // Chart modal handlers
  const handleViewChart = (chart) => {
    setSelectedChart(chart);
    setShowChartModal(true);
  };

  const handleCloseChartModal = () => {
    setShowChartModal(false);
    setSelectedChart(null);
  };

  const toggleChartsSection = () => {
    setShowChartsSection(!showChartsSection);
  };

  useEffect(() => {
    if (reportId) {
      loadReportData();
    }
  }, [reportId, dataLimit]);

  useEffect(() => {
    setRuntimeSort(null);
  }, [reportId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const list = res?.data?.data || res?.data || [];
        const arr = Array.isArray(list) ? list : [];
        const baseRow = arr.find((r) => r.is_base_currency === true);
        if (!cancelled) {
          setBaseCurrencyCode(
            baseRow?.currency_code
              ? String(baseRow.currency_code).trim()
              : null
          );
          setExchangeRateLookup(buildExchangeRateLookupFromCurrencyList(arr));
        }
      } catch (e) {
        console.error('ReportDataViewer: failed to load base currency', e);
        if (!cancelled) {
          setExchangeRateLookup(null);
        }
      }
      try {
        const s = await getMultiCurrencySettings();
        if (!cancelled) setMultiCurrencyEnabled(!!s.multiCurrencyEnabled);
      } catch {
        if (!cancelled) setMultiCurrencyEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const loadReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, get the report configuration
      const reportBuilderApi = new DataApi('reportbuilder');
      const reportResponse = await reportBuilderApi.get(`/${reportId}`);

      if (!reportResponse?.data?.success) {
        throw new Error('Failed to load report configuration');
      }

      const report = reportResponse.data.data;
      console.log('Loaded report for viewer:', report);

      // Set report information
      setReportInfo({
        id: report.id,
        name: report.name,
        report_label: report.report_label,
        description: report.description,
        object_name: report.object_name,
        object_label: report.object_label,
        report_type: report.report_type,
        is_public: report.is_public,
        filter_logic: report.filter_logic,
        filter_custom_logic: report.filter_custom_logic,
        report_objects: report.report_objects || []
      });

      // Process report fields and groups
      if (report.fields && report.fields.length > 0) {
        const fields = report.fields.map(field => ({
          id: field.field_config_id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          is_aggregated: field.is_aggregated,
          aggregate_function: field.aggregate_function,
          is_grouped: field.is_grouped,
          sort_order: field.sort_order,
          display_config: field.display_config,
          displayConfig: field.display_config,
          scale: field.scale,
        }));

        // Helper function to convert database aggregation values to dropdown values
        const convertAggregationValue = (dbValue) => {
          const mapping = {
            'average': 'avg',
            'sum': 'sum',
            'min': 'min',
            'max': 'max',
            'count': 'count'
          };
          return mapping[dbValue] || dbValue;
        };

        // Set summarized fields
        const summarized = {};
        fields.forEach(field => {
          if (field.is_aggregated && field.aggregate_function) {
            const convertedValue = convertAggregationValue(field.aggregate_function);
            summarized[field.id] = {
              aggregation: convertedValue
            };
          }
        });
        setSummarizedFields(summarized);

        // Separate fields into selected fields and groups
        const selectedFieldsList = fields.filter(field => !field.is_grouped).map(field => ({
          ...field,
          summaryType: field.is_aggregated && field.aggregate_function ? convertAggregationValue(field.aggregate_function) : 'none'
        }));
        const groups = fields.filter(field => field.is_grouped).map(field => ({
          ...field,
          summaryType: field.is_aggregated && field.aggregate_function ? convertAggregationValue(field.aggregate_function) : 'none'
        }));

        setSelectedFields(selectedFieldsList);
        setSelectedGroups(groups);

        // Load charts from the report data
        console.log('ReportDataViewer - Report charts data:', report.charts);
        console.log('ReportDataViewer - Is charts array?', Array.isArray(report.charts));
        console.log('ReportDataViewer - Charts length:', report.charts ? report.charts.length : 'undefined');

        if (report.charts && Array.isArray(report.charts) && report.charts.length > 0) {
          console.log('ReportDataViewer - Setting charts from report data:', report.charts);
          setCharts(report.charts);
          // Notify parent component about charts
          if (onChartsLoaded) {
            onChartsLoaded(report.charts.length);
          }
        } else {
          console.log('ReportDataViewer - No charts found in report data');
          setCharts([]);
          // Notify parent component that there are no charts
          if (onChartsLoaded) {
            onChartsLoaded(0);
          }
        }
      }
      console.log('report:', report.filters);
      // Process report filters
      if (report.filters && report.filters.length > 0) {
        const filters = report.filters
          .filter(filter => filter.field_name && filter.field_config_id) // Only include valid filters
          .map(filter => ({
            id: filter.id,
            field_config_id: filter.field_config_id,
            field_name: filter.field_name,
            field_label: filter.field_label,
            data_type: filter.data_type,
            object_name: filter.object_name,
            operator: filter.operator || 'equals',
            value: filter.value || '',
            is_editable: filter.is_editable !== false,
            order_no: filter.order_no || 1,
            picklist_values: filter.picklist_values || []
          }));

        setReportFilters(filters);
        console.log('Loaded report filters:', filters);
        console.log('Filter count:', filters.length);
        filters.forEach((filter, index) => {
          console.log(`Loaded Filter ${index + 1}:`, {
            field_name: filter.field_name,
            is_editable: filter.is_editable,
            value: filter.value,
            operator: filter.operator
          });
        });
      } else {
        setReportFilters([]);
        console.log('No filters found in report');
      }

      // Notify parent component about filter count
      if (onFiltersLoaded) {
        const filterCount = report.filters ? report.filters.length : 0;
        console.log('ReportDataViewer: Notifying parent about filter count:', filterCount);
        onFiltersLoaded(filterCount);
      }

      // Now fetch the actual report data using the new route
      await fetchReportData(report);

    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = async (report, filters = {}) => {
    setDataLoading(true);
    console.log('fetchReportData:', {
      reportId: reportId,
      limit: dataLimit,
      filters: filters
    });
    try {
      // Use the new route that will fetch report data and call executePreviewQuery
      const dataApi = new DataApi('object');
      const response = await dataApi.post('/view/report', {
        reportId: reportId,
        limit: dataLimit,
        filters: filters
      });

      if (response?.data?.success) {
        const rawData = response.data.data;
        const grouping = response.data.grouping;

        console.log('Report data loaded:', rawData);
        console.log('Grouping info:', grouping);

        setPreviewData(rawData);
        setGrouping(grouping);

      } else {
        throw new Error(response?.data?.message || 'Failed to fetch report data');
      }

    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.message);
    } finally {
      setDataLoading(false);
    }
  };

  // Helper function to check if a field is grouped
  const isFieldGrouped = (field) => {
    return selectedGroups.some(group => group.id === field.id);
  };

  // Create grouping information from selectedGroups
  const groupingInfo = {
    hasGrouping: selectedGroups.length > 0,
    groupedFields: selectedGroups.map(group => ({
      field_name: group.field_name,
      field_label: group.field_label,
      object_name: group.object_name,
      data_type: group.data_type,
      id: group.id
    }))
  };

  const cycleRuntimeSortForField = fieldId => {
    setRuntimeSort(prev => {
      if (prev && String(prev.fieldId) === String(fieldId)) {
        if (prev.direction === 'asc') return { fieldId, direction: 'desc' };
        return null;
      }
      return { fieldId, direction: 'asc' };
    });
  };

  const runtimeSortIndicator = fieldId => {
    if (!runtimeSort || String(runtimeSort.fieldId) !== String(fieldId)) {
      return null;
    }
    return runtimeSort.direction === 'desc' ? '\u2193' : '\u2191';
  };

  if (loading) {
    return (
      <div className="report-data-viewer">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-data-viewer">
        <div className="error-container">
          <h3>Error Loading Report</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!reportInfo || !selectedFields.length) {
    return (
      <div className="report-data-viewer">
        <div className="empty-container">
          <h3>No Report Data</h3>
          <p>No report configuration or fields found.</p>
        </div>
      </div>
    );
  }

  const reportConversionBaseCode = multiCurrencyEnabled ? baseCurrencyCode : null;
  const reportConversionLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  // Helper function to get field value from row (same as in renderGroupedData)
  const getFieldValueForExport = (field, row) => {
    const tableName = field.object_name || 'main';
    const aliasedFieldName = `${tableName}_${field.field_name}`;
    const fieldData = row[aliasedFieldName] || row[field.field_name];

    if (fieldData) {
      if (typeof fieldData === 'object' && fieldData !== null) {
        return fieldData.displayValue || fieldData.value || 'N/A';
      } else {
        return fieldData;
      }
    }
    return 'N/A';
  };

  const getAggExportNum = (field, row) =>
    getAggregatableNumericForExport(
      field,
      row,
      reportConversionBaseCode,
      getFieldValueForExport,
      reportConversionLookup
    );

  // Helper function to format values specifically for PDF export (handles currency symbols better)
  const formatValueForPDF = (fieldData, dataType) => {
    // Handle enriched data structure (from backend with display values)
    if (typeof fieldData === 'object' && fieldData !== null) {
      // If it's a lookup field with display value, show the display value
      if (fieldData.isReference && fieldData.displayValue) {
        return fieldData.displayValue;
      }
      // If it's a picklist field with display value, show the display value
      if ((fieldData.isPicklist || fieldData.isMultiPicklist) && fieldData.displayValue) {
        return fieldData.displayValue;
      }
      // Otherwise, use the value property
      const value = fieldData.value;
      if (value === null || value === undefined || value === '') return 'N/A';

      switch (dataType) {
        case 'currency':
          const numValue = parseFloat(value);
          if (isNaN(numValue)) return 'N/A';
          // Use 'INR' for PDF compatibility
          return `INR ${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        case 'number':
        case 'decimal':
          const numVal = parseFloat(value);
          if (isNaN(numVal)) return 'N/A';
          return numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        case 'percent':
          const percentVal = parseFloat(value);
          if (isNaN(percentVal)) return 'N/A';
          return `${percentVal.toFixed(2)}%`;
        default:
          return value.toString();
      }
    }

    // Handle primitive values
    if (fieldData === null || fieldData === undefined || fieldData === '') return 'N/A';

    switch (dataType) {
      case 'currency':
        const numValue = parseFloat(fieldData);
        if (isNaN(numValue)) return 'N/A';
        // Use 'INR' for PDF compatibility
        return `INR ${numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'number':
      case 'decimal':
        const numVal = parseFloat(fieldData);
        if (isNaN(numVal)) return 'N/A';
        return numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      case 'percent':
        const percentVal = parseFloat(fieldData);
        if (isNaN(percentVal)) return 'N/A';
        return `${percentVal.toFixed(2)}%`;
      default:
        return fieldData.toString();
    }
    return 'N/A';
  };

  const handleFilterChange = (filterId, value) => {
    setAppliedFilters(prev => ({
      ...prev,
      [filterId]: value === '' ? null : value
    }));
  };

  const applyFilters = async () => {
    if (!reportInfo) return;

    try {
      // Transform appliedFilters to the format expected by backend
      const backendFilters = {};

      console.log('Total reportFilters:', reportFilters.length);
      reportFilters.forEach((filter, index) => {
        console.log(`Filter ${index + 1}:`, {
          field_name: filter.field_name,
          is_editable: filter.is_editable,
          value: filter.value,
          operator: filter.operator
        });

        const operatorKey = `${filter.id}_operator`;
        const valueKey = `${filter.id}_value`;

        const operator = appliedFilters[operatorKey] !== undefined ? appliedFilters[operatorKey] : filter.operator;
        const value = appliedFilters[valueKey] !== undefined ? appliedFilters[valueKey] : filter.value;

        // Include all filters regardless of value or disabled state
        // Use filter.id as key to avoid collisions when multiple filters have same field_name
        const filterKey = filter.id || `${filter.field_name}_${index}`;
        backendFilters[filterKey] = {
          ...filter, // Preserve all original filter properties
          operator: operator,
          value: value
        };
      });

      console.log('Applying filters:', backendFilters);

      // Convert backendFilters object to array format matching backend structure
      const filtersArray = Object.values(backendFilters);
      console.log('Filters array:', filtersArray);

      await fetchReportData(reportInfo, filtersArray);
      if (onFilterToggle) {
        onFilterToggle();
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      setError('Failed to apply filters. Please try again.');
    }
  };

  const resetFilters = () => {
    setAppliedFilters({});
  };



  // Export functions
  const exportToPDF = () => {
    try {
      console.log('exportToPDF function called');
      console.log('previewData:', previewData);
      console.log('selectedFields:', selectedFields);
      console.log('grouping:', grouping);

      const groupingFields = grouping.groupedFields;
      console.log('groupingFields:', groupingFields);
      // Helper function to get field value from row
      const getFieldValue = (field, row) => {
        const tableName = field.object_name || 'main';
        const aliasedFieldName = `${tableName}_${field.field_name}`;
        const fieldData = row[aliasedFieldName] || row[field.field_name];

        if (fieldData) {
          if (typeof fieldData === 'object' && fieldData !== null) {
            return fieldData.displayValue || fieldData.value || 'N/A';
          } else {
            return fieldData;
          }
        }
        return 'N/A';
      };

      // Simple groupByFields function
      function groupByFields(data, groupFields) {
        return data.reduce((acc, row) => {
          let currentLevel = acc;
          groupFields.forEach((field, index) => {
            const fieldValue = getFieldValue(field, row);

            // if value not initialized, create new object/array
            if (!currentLevel[fieldValue]) {
              currentLevel[fieldValue] = (index === groupFields.length - 1) ? [] : {};
            }

            // go deeper
            if (index === groupFields.length - 1) {
              currentLevel[fieldValue].push(row);
            } else {
              currentLevel = currentLevel[fieldValue];
            }
          });

          return acc;
        }, {});
      }

      const nestedGroups = groupByFields(previewData, groupingFields);

      console.log('nestedGroups:', nestedGroups);
      const printedGroups = {};
      const calculatedGroup = {};

      const initializeGroups = (groups, fieldIndex = 0, parentPath = '') => {
        for (const key in groups) {
          const value = groups[key];
          const field = groupingFields[fieldIndex];
          const currentPath = parentPath ? `${parentPath}|${key}` : key;
          const compositeKey = `${field.field_name}_${currentPath}`;

          if (Array.isArray(value)) {
            printedGroups[compositeKey] = false;
            calculatedGroup[compositeKey] = 0;
          } else if (typeof value === 'object' && value !== null) {
            printedGroups[compositeKey] = false;
            calculatedGroup[compositeKey] = 0;
            initializeGroups(value, fieldIndex + 1, currentPath);
          }
        }
      };

      initializeGroups(nestedGroups);

      // Helper function to find the hierarchical path for a row in nestedGroups
      const findRowPath = (row, groups, fieldIndex = 0, currentPath = '') => {
        const field = groupingFields[fieldIndex];
        const fieldValue = getFieldValue(field, row);

        if (!fieldValue || !groups[fieldValue]) {
          return null;
        }

        const newPath = currentPath ? `${currentPath}|${fieldValue}` : fieldValue;

        if (fieldIndex === groupingFields.length - 1) {
          const rows = groups[fieldValue];
          if (Array.isArray(rows) && rows.includes(row)) {
            return newPath;
          }
        } else {
          return findRowPath(row, groups[fieldValue], fieldIndex + 1, newPath);
        }

        return null;
      };

      // Fill the counts using hierarchical composite keys
      previewData.forEach((row) => {
        const rowPath = findRowPath(row, nestedGroups);
        if (rowPath) {
          groupingFields.forEach((field, fieldIndex) => {
            const fieldValue = getFieldValue(field, row);
            if (fieldValue) {
              const pathParts = rowPath.split('|');
              const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
              const compositeKey = `${field.field_name}_${currentPath}`;

              if (calculatedGroup.hasOwnProperty(compositeKey)) {
                calculatedGroup[compositeKey]++;
              }
            }
          });
        }
      });
      console.log('## calculatedGroup', calculatedGroup);




      const doc = new jsPDF('landscape', 'mm', 'a4');

      // Add title
      const reportTitle = reportInfo.report_label || reportInfo.name || 'Report';
      const objectInfo = reportInfo.report_objects && reportInfo.report_objects.length > 0
        ? reportInfo.report_objects.map(obj => obj.object_label || obj.object_name).join(' & ')
        : reportInfo.object_label || reportInfo.object_name || 'Unknown Object';

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`${reportTitle} - ${objectInfo}`, 14, 20);

      // Add metadata
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
      doc.text(`Total Records: ${previewData.length}`, 14, 35);

      // Prepare headers
      const headers = [];

      // Add grouped fields first
      if (grouping && grouping.hasGrouping && grouping.groupedFields) {
        grouping.groupedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });

        // Add non-grouped field headers
        selectedFields.filter(field =>
          !grouping.groupedFields.some(groupField =>
            groupField.field_name === field.field_name &&
            groupField.object_name === field.object_name
          )
        ).forEach(field => {
          headers.push(field.field_label || field.field_name);
        });
      } else {
        // No grouping, show all fields normally
        selectedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });
      }

      // Prepare data with subtotals and grand totals (same logic as Formatted Excel)
      const tableData = [];
      const groupedFields = grouping && grouping.hasGrouping && grouping.groupedFields ? grouping.groupedFields : [];

      if (grouping && grouping.hasGrouping && grouping.groupedFields) {
        // Create nested grouped data structure (same as Excel export)
        const groupedData = {};

        // Group the data
        previewData.forEach(row => {
          let currentGroup = groupedData;

          // Navigate/create group hierarchy
          groupedFields.forEach((field, fieldIndex) => {
            const fieldValue = getFieldValueForExport(field, row);

            if (!currentGroup[fieldValue]) {
              currentGroup[fieldValue] = fieldIndex === groupedFields.length - 1 ? [] : {};
            }

            if (fieldIndex === groupedFields.length - 1) {
              currentGroup[fieldValue].push(row);
            } else {
              currentGroup = currentGroup[fieldValue];
            }
          });
        });

        // Helper function to recursively process grouped data with subtotals
        const processGroupedDataWithSubtotals = (groups, level = 0, parentKey = '') => {
          Object.keys(groups).forEach(groupKey => {
            const groupValue = groups[groupKey];

            if (Array.isArray(groupValue)) {
              // Last level - process data rows
              groupValue.forEach(row => {
                const rowData = [];

                // Add grouped field values
                groupedFields.forEach(field => {
                  const value = getFieldValueForExport(field, row);
                  rowData.push(formatValueForPDF(value, field.data_type));
                });

                // Add non-grouped field values
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  const value = getFieldValueForExport(field, row);
                  rowData.push(formatValueForPDF(value, field.data_type));
                });

                tableData.push(rowData);
              });

              // Add subtotal for this group (last level)
              if (Object.keys(summarizedFields).length > 0) {
                const subtotalRow = [];

                // Add empty cells for grouped fields, show 'Subtotal' at current level
                groupedFields.forEach((field, fieldIndex) => {
                  if (fieldIndex === level) {
                    // Use calculatedGroup count like in UI display
                    let storedCount = 0;
                    if (Array.isArray(groupValue) && groupValue.length > 0) {
                      const firstRow = groupValue[0];
                      const currentValue = getFieldValue(field, firstRow);
                      const rowPath = findRowPath(firstRow, nestedGroups);

                      // Create composite key matching the structure in calculatedGroup
                      let compositeKey;
                      if (level === 0) {
                        // First level: field_name + value
                        compositeKey = `${field.field_name}_${currentValue}`;
                      } else {
                        // Subsequent levels: field_name + path
                        const pathParts = rowPath.split('|');
                        const currentPath = pathParts.slice(0, level + 1).join('|');
                        compositeKey = `${field.field_name}_${currentPath}`;
                      }

                      storedCount = calculatedGroup[compositeKey] || 0;
                      console.log('PDF last level subtotal debug:', { level, currentValue, rowPath, compositeKey, storedCount });
                    }
                    console.log('## count', `Subtotal (${storedCount})`);
                    subtotalRow.push(`Subtotal (${storedCount})`);
                  } else {
                    subtotalRow.push('');
                  }
                });

                // Add subtotals for summarized fields
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  if (summarizedFields[field.id]) {
                    const aggregation = summarizedFields[field.id].aggregation;
                    let subtotalValue = '';

                    switch (aggregation) {
                      case 'sum':
                        subtotalValue = groupValue.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        break;
                      case 'avg':
                        const sum = groupValue.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        subtotalValue = groupValue.length > 0 ? sum / groupValue.length : 0;
                        break;
                      case 'count':
                        subtotalValue = groupValue.length;
                        break;
                      case 'min':
                        subtotalValue = Math.min(...groupValue.map(row => getAggExportNum(field, row)));
                        break;
                      case 'max':
                        subtotalValue = Math.max(...groupValue.map(row => getAggExportNum(field, row)));
                        break;
                    }

                    subtotalRow.push(formatValueForPDF(subtotalValue, field.data_type));
                  } else {
                    subtotalRow.push('');
                  }
                });

                tableData.push(subtotalRow);
              }
            } else {
              // Not last level - recursively process sub-groups
              processGroupedDataWithSubtotals(groupValue, level + 1, parentKey ? `${parentKey}|${groupKey}` : groupKey);

              // Add subtotal for this group level (intermediate level)
              if (Object.keys(summarizedFields).length > 0) {
                const subtotalRow = [];

                console.log("Grouped Fields", groupedFields);

                // Add empty cells for grouped fields, show 'Subtotal' at current level
                groupedFields.forEach((field, fieldIndex) => {
                  console.log("Field Index", fieldIndex, "Level", level);
                  if (fieldIndex === level) {
                    // Use calculatedGroup count like in UI display
                    let storedCount = 0;
                    if (groupValue && (Array.isArray(groupValue) ? groupValue.length > 0 : Object.keys(groupValue).length > 0)) {
                      let currentValue, rowPath;

                      if (Array.isArray(groupValue)) {
                        // Last level - value is an array of records
                        const firstRow = groupValue[0];
                        currentValue = getFieldValue(field, firstRow);
                        rowPath = findRowPath(firstRow, nestedGroups);
                      } else {
                        // Intermediate level - value is an object with nested groups
                        currentValue = groupKey; // Use the key directly since it represents the group value

                        // Find a sample row from the nested data to get the path
                        const sampleRow = Object.values(groupValue)[0]?.[0]; // Get first record from first nested group
                        if (sampleRow) {
                          rowPath = findRowPath(sampleRow, nestedGroups);
                        }
                      }

                      // Create composite key matching the structure in calculatedGroup
                      let compositeKey;
                      if (level === 0) {
                        // First level: field_name + value
                        compositeKey = `${field.field_name}_${currentValue}`;
                      } else {
                        // Subsequent levels: field_name + path
                        const pathParts = rowPath.split('|');
                        const currentPath = pathParts.slice(0, level + 1).join('|');
                        compositeKey = `${field.field_name}_${currentPath}`;
                      }

                      storedCount = calculatedGroup[compositeKey] || 0;
                      console.log('PDF intermediate subtotal debug:', { level, currentValue, rowPath, compositeKey, storedCount });
                    }

                    subtotalRow.push(`Subtotal (${storedCount})`);
                    console.log('## groupValue Row', groupValue);
                  } else {
                    subtotalRow.push('');
                  }
                });

                // Calculate subtotal for this group - get all rows from nested structure
                const getAllRows = (obj) => {
                  let allRows = [];
                  for (const subKey in obj) {
                    const subValue = obj[subKey];
                    if (Array.isArray(subValue)) {
                      allRows = allRows.concat(subValue);
                    } else if (typeof subValue === 'object' && subValue !== null) {
                      allRows = allRows.concat(getAllRows(subValue));
                    }
                  }
                  return allRows;
                };

                const groupData = getAllRows(groupValue);

                // Add subtotals for summarized fields
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  if (summarizedFields[field.id]) {
                    const aggregation = summarizedFields[field.id].aggregation;
                    let subtotalValue = '';

                    switch (aggregation) {
                      case 'sum':
                        subtotalValue = groupData.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        break;
                      case 'avg':
                        const sum = groupData.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        subtotalValue = groupData.length > 0 ? sum / groupData.length : 0;
                        break;
                      case 'count':
                        subtotalValue = groupData.length;
                        break;
                      case 'min':
                        subtotalValue = Math.min(...groupData.map(row => getAggExportNum(field, row)));
                        break;
                      case 'max':
                        subtotalValue = Math.max(...groupData.map(row => getAggExportNum(field, row)));
                        break;
                    }

                    subtotalRow.push(formatValueForPDF(subtotalValue, field.data_type));
                  } else {
                    subtotalRow.push('');
                  }
                });

                tableData.push(subtotalRow);
              }
            }
          });
        };

        // Process grouped data with subtotals
        processGroupedDataWithSubtotals(groupedData);

        // Add grand total row
        if (Object.keys(summarizedFields).length > 0) {
          const grandTotalRow = [];

          // Add 'GRAND TOTAL' in first cell
          grandTotalRow.push('GRAND TOTAL');

          // Add empty cells for remaining grouped fields
          for (let i = 1; i < groupedFields.length; i++) {
            grandTotalRow.push('');
          }

          // Add grand totals for summarized fields
          selectedFields.filter(field =>
            !groupedFields.some(groupField =>
              groupField.field_name === field.field_name &&
              groupField.object_name === field.object_name
            )
          ).forEach(field => {
            if (summarizedFields[field.id]) {
              const aggregation = summarizedFields[field.id].aggregation;
              let grandTotalValue = '';

              switch (aggregation) {
                case 'sum':
                  grandTotalValue = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  break;
                case 'avg':
                  const sum = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  grandTotalValue = previewData.length > 0 ? sum / previewData.length : 0;
                  break;
                case 'count':
                  grandTotalValue = previewData.length;
                  break;
                case 'min':
                  grandTotalValue = Math.min(...previewData.map(row => getAggExportNum(field, row)));
                  break;
                case 'max':
                  grandTotalValue = Math.max(...previewData.map(row => getAggExportNum(field, row)));
                  break;
              }

              grandTotalRow.push(formatValueForPDF(grandTotalValue, field.data_type));
            } else {
              grandTotalRow.push('');
            }
          });

          tableData.push(grandTotalRow);
        }
      } else {
        // No grouping - just add data rows
        previewData.forEach(row => {
          const rowData = [];

          selectedFields.forEach(field => {
            const value = getFieldValueForExport(field, row);
            rowData.push(formatValueForPDF(value, field.data_type));
          });

          tableData.push(rowData);
        });

        // Add grand total row for non-grouped data
        if (Object.keys(summarizedFields).length > 0) {
          const grandTotalRow = [];

          // Add 'GRAND TOTAL' in first cell
          grandTotalRow.push('GRAND TOTAL');

          // Add grand totals for summarized fields
          selectedFields.forEach(field => {
            if (summarizedFields[field.id]) {
              const aggregation = summarizedFields[field.id].aggregation;
              let grandTotalValue = '';

              switch (aggregation) {
                case 'sum':
                  grandTotalValue = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  break;
                case 'avg':
                  const sum = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  grandTotalValue = previewData.length > 0 ? sum / previewData.length : 0;
                  break;
                case 'count':
                  grandTotalValue = previewData.length;
                  break;
                case 'min':
                  grandTotalValue = Math.min(...previewData.map(row => getAggExportNum(field, row)));
                  break;
                case 'max':
                  grandTotalValue = Math.max(...previewData.map(row => getAggExportNum(field, row)));
                  break;
              }

              grandTotalRow.push(formatValueForPDF(grandTotalValue, field.data_type));
            } else {
              grandTotalRow.push('');
            }
          });

          tableData.push(grandTotalRow);
        }
      }

      // Generate table with custom row styling
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 50,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          font: 'helvetica',
          halign: 'left'
        },
        headStyles: {
          fillColor: [128, 128, 128], // Gray header
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          font: 'helvetica',
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
          font: 'helvetica',
          halign: 'left'
        },
        didParseCell: function (data) {
          // Ensure proper font for all cells
          data.cell.styles.font = 'helvetica';

          // Get the field info to determine data type
          const fieldIndex = data.column.index;
          let shouldRightAlign = false;

          // Check if this column should be right-aligned based on field type
          if (groupedFields && groupedFields.length > 0) {
            if (fieldIndex < groupedFields.length) {
              // This is a grouped field - check its data type
              const field = groupedFields[fieldIndex];
              shouldRightAlign = ['number', 'decimal', 'currency'].includes(field.data_type);
            } else {
              // This is a non-grouped field - check its data type
              const nonGroupedFields = selectedFields.filter(field =>
                !groupedFields.some(groupField =>
                  groupField.field_name === field.field_name &&
                  groupField.object_name === field.object_name
                )
              );
              const adjustedIndex = fieldIndex - groupedFields.length;
              if (nonGroupedFields[adjustedIndex]) {
                const field = nonGroupedFields[adjustedIndex];
                shouldRightAlign = ['number', 'decimal', 'currency'].includes(field.data_type);
              }
            }
          } else {
            // No grouping - check field data type
            if (selectedFields[fieldIndex]) {
              const field = selectedFields[fieldIndex];
              shouldRightAlign = ['number', 'decimal', 'currency'].includes(field.data_type);
            }
          }

          // Set alignment based on data type
          data.cell.styles.halign = shouldRightAlign ? 'right' : 'left';

          // Style subtotal and grand total rows
          if (data.row.raw && data.row.raw.some(cell => typeof cell === 'string' && cell.includes('Subtotal'))) {
            data.cell.styles.fillColor = [220, 220, 220]; // Light gray for subtotals
            data.cell.styles.fontStyle = 'bold';
            console.log('PDF: Styling subtotal row:', data.row.raw);
          } else if (data.row.raw && data.row.raw[0] === 'GRAND TOTAL') {
            data.cell.styles.fillColor = [128, 128, 128]; // Dark gray for grand totals
            data.cell.styles.textColor = [255, 255, 255]; // White text
            data.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { top: 50 }
      });

      // Save the PDF
      const fileName = `${(reportInfo.report_label || reportInfo.name).replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const exportToPDFWithCharts = async () => {
    try {
      console.log('exportToPDFWithCharts function called');
      console.log('Charts available:', charts);
      console.log('Charts length:', charts ? charts.length : 0);

      if (!charts || charts.length === 0) {
        console.log('No charts available, falling back to regular PDF export');
        exportToPDF();
        return;
      }

      // Create a new PDF document
      const doc = new jsPDF('landscape', 'mm', 'a4');

      // Add title
      const reportTitle = reportInfo.report_label || reportInfo.name || 'Report';
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(reportTitle, 20, 20);

      // Add subtitle with charts info
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report with Charts - Generated on ${new Date().toLocaleDateString()}`, 20, 30);

      let currentY = 50;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const maxWidth = doc.internal.pageSize.width - (margin * 2);

      // Capture and add charts
      for (let i = 0; i < charts.length; i++) {
        const chart = charts[i];
        console.log(`Processing chart ${i + 1}:`, chart.title);

        try {
          // Find the chart element in the DOM
          const chartElement = document.querySelector(`[data-chart-id="${chart.id}"]`);
          if (!chartElement) {
            console.warn(`Chart element not found for chart ${chart.id}, skipping...`);
            continue;
          }

          // Hide chart metadata before capture
          const metadataElement = chartElement.querySelector('.chart-metadata');
          let wasHidden = false;
          if (metadataElement) {
            metadataElement.style.display = 'none';
            wasHidden = true;
          }

          // Capture the chart as an image
          const canvas = await html2canvas(chartElement, {
            backgroundColor: '#ffffff',
            scale: 2, // Higher resolution
            useCORS: true,
            allowTaint: true,
            logging: false
          });

          // Restore chart metadata visibility
          if (wasHidden && metadataElement) {
            metadataElement.style.display = '';
          }

          const imgData = canvas.toDataURL('image/png');

          // Calculate dimensions to fit on page
          const imgWidth = Math.min(maxWidth, canvas.width * 0.5); // Scale down
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Check if we need a new page
          if (currentY + imgHeight + 20 > pageHeight - margin) {
            doc.addPage();
            currentY = margin;
          }

          // Add chart title
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(chart.title || `Chart ${i + 1}`, margin, currentY);
          currentY += 10;

          // Add the chart image
          doc.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 20;

          console.log(`Chart ${i + 1} added to PDF successfully`);

        } catch (chartError) {
          console.error(`Error capturing chart ${i + 1}:`, chartError);
          // Continue with other charts
        }
      }

      // Add a new page for the data table
      doc.addPage();
      currentY = 20;

      // Add data table title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Report Data', margin, currentY);
      currentY += 15;

      // Generate the data table (reuse the existing PDF table logic)
      const headers = [];
      const tableData = [];

      // Prepare headers and data (same logic as exportToPDF)
      if (grouping && grouping.hasGrouping && grouping.groupedFields) {
        const groupedFields = grouping.groupedFields;

        // Add grouped field headers
        groupedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });

        // Add non-grouped field headers
        selectedFields.filter(field =>
          !groupedFields.some(groupField =>
            groupField.field_name === field.field_name &&
            groupField.object_name === field.object_name
          )
        ).forEach(field => {
          headers.push(field.field_label || field.field_name);
        });

        // Create grouped data structure and add to table
        const groupedData = {};
        previewData.forEach(row => {
          let currentGroup = groupedData;
          groupedFields.forEach((field, fieldIndex) => {
            const fieldValue = getFieldValueForExport(field, row);
            if (!currentGroup[fieldValue]) {
              currentGroup[fieldValue] = fieldIndex === groupedFields.length - 1 ? [] : {};
            }
            if (fieldIndex === groupedFields.length - 1) {
              currentGroup[fieldValue].push(row);
            } else {
              currentGroup = currentGroup[fieldValue];
            }
          });
        });

        // Process grouped data and add to table
        const processGroupedData = (groups, level = 0) => {
          Object.keys(groups).forEach(groupKey => {
            const groupValue = groups[groupKey];

            if (Array.isArray(groupValue)) {
              // Last level - process data rows
              groupValue.forEach(row => {
                const rowData = [];
                groupedFields.forEach(field => {
                  const value = getFieldValueForExport(field, row);
                  rowData.push(formatValueForPDF(value, field.data_type));
                });
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  const value = getFieldValueForExport(field, row);
                  rowData.push(formatValueForPDF(value, field.data_type));
                });
                tableData.push(rowData);
              });

              // Add subtotal for this group
              if (Object.keys(summarizedFields).length > 0) {
                const subtotalRow = [];
                subtotalRow.push(`Subtotal (${groupValue.length})`);
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  if (summarizedFields[field.id]) {
                    const aggregation = summarizedFields[field.id].aggregation;
                    let subtotalValue = '';

                    switch (aggregation) {
                      case 'sum':
                        subtotalValue = groupValue.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        break;
                      case 'avg':
                        const sum = groupValue.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        subtotalValue = groupValue.length > 0 ? sum / groupValue.length : 0;
                        break;
                      case 'count':
                        subtotalValue = groupValue.length;
                        break;
                      case 'min':
                        subtotalValue = Math.min(...groupValue.map(row => getAggExportNum(field, row)));
                        break;
                      case 'max':
                        subtotalValue = Math.max(...groupValue.map(row => getAggExportNum(field, row)));
                        break;
                    }
                    subtotalRow.push(formatValueForPDF(subtotalValue, field.data_type));
                  } else {
                    subtotalRow.push('');
                  }
                });
                tableData.push(subtotalRow);
              }
            } else {
              // Intermediate level - process nested groups
              processGroupedData(groupValue, level + 1);
            }
          });
        };

        processGroupedData(groupedData);

        // Add grand total
        if (Object.keys(summarizedFields).length > 0) {
          const grandTotalRow = [];
          grandTotalRow.push('GRAND TOTAL');
          selectedFields.filter(field =>
            !groupedFields.some(groupField =>
              groupField.field_name === field.field_name &&
              groupField.object_name === field.object_name
            )
          ).forEach(field => {
            if (summarizedFields[field.id]) {
              const aggregation = summarizedFields[field.id].aggregation;
              let grandTotalValue = '';

              switch (aggregation) {
                case 'sum':
                  grandTotalValue = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  break;
                case 'avg':
                  const sum = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  grandTotalValue = previewData.length > 0 ? sum / previewData.length : 0;
                  break;
                case 'count':
                  grandTotalValue = previewData.length;
                  break;
                case 'min':
                  grandTotalValue = Math.min(...previewData.map(row => getAggExportNum(field, row)));
                  break;
                case 'max':
                  grandTotalValue = Math.max(...previewData.map(row => getAggExportNum(field, row)));
                  break;
              }
              grandTotalRow.push(formatValueForPDF(grandTotalValue, field.data_type));
            } else {
              grandTotalRow.push('');
            }
          });
          tableData.push(grandTotalRow);
        }
      } else {
        // No grouping - add all fields normally
        selectedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });

        // Add data rows
        previewData.forEach(row => {
          const rowData = [];
          selectedFields.forEach(field => {
            const value = getFieldValueForExport(field, row);
            rowData.push(formatValueForPDF(value, field.data_type));
          });
          tableData.push(rowData);
        });

        // Add grand total for regular data
        if (Object.keys(summarizedFields).length > 0) {
          const grandTotalRow = [];
          grandTotalRow.push('GRAND TOTAL');
          selectedFields.forEach(field => {
            if (summarizedFields[field.id]) {
              const aggregation = summarizedFields[field.id].aggregation;
              let grandTotalValue = '';

              switch (aggregation) {
                case 'sum':
                  grandTotalValue = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  break;
                case 'avg':
                  const sum = previewData.reduce((sum, row) => {
                    const val = getAggExportNum(field, row);
                    return sum + val;
                  }, 0);
                  grandTotalValue = previewData.length > 0 ? sum / previewData.length : 0;
                  break;
                case 'count':
                  grandTotalValue = previewData.length;
                  break;
                case 'min':
                  grandTotalValue = Math.min(...previewData.map(row => getAggExportNum(field, row)));
                  break;
                case 'max':
                  grandTotalValue = Math.max(...previewData.map(row => getAggExportNum(field, row)));
                  break;
              }
              grandTotalRow.push(formatValueForPDF(grandTotalValue, field.data_type));
            } else {
              grandTotalRow.push('');
            }
          });
          tableData.push(grandTotalRow);
        }
      }

      // Generate table with custom row styling
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: currentY,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          font: 'helvetica',
          halign: 'left'
        },
        headStyles: {
          fillColor: [128, 128, 128],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          font: 'helvetica',
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
          font: 'helvetica',
          halign: 'left'
        },
        didParseCell: function (data) {
          data.cell.styles.font = 'helvetica';

          const fieldIndex = data.column.index;
          let shouldRightAlign = false;

          if (grouping && grouping.hasGrouping && grouping.groupedFields) {
            const groupedFields = grouping.groupedFields;
            if (fieldIndex < groupedFields.length) {
              const field = groupedFields[fieldIndex];
              shouldRightAlign = ['number', 'decimal', 'currency'].includes(field.data_type);
            } else {
              const nonGroupedFields = selectedFields.filter(field =>
                !groupedFields.some(groupField =>
                  groupField.field_name === field.field_name &&
                  groupField.object_name === field.object_name
                )
              );
              const adjustedIndex = fieldIndex - groupedFields.length;
              if (nonGroupedFields[adjustedIndex]) {
                const field = nonGroupedFields[adjustedIndex];
                shouldRightAlign = ['number', 'decimal', 'currency'].includes(field.data_type);
              }
            }
          } else {
            if (selectedFields[fieldIndex]) {
              const field = selectedFields[fieldIndex];
              shouldRightAlign = ['number', 'decimal', 'currency'].includes(field.data_type);
            }
          }

          data.cell.styles.halign = shouldRightAlign ? 'right' : 'left';

          if (data.row.raw && data.row.raw.some(cell => typeof cell === 'string' && cell.includes('Subtotal'))) {
            data.cell.styles.fillColor = [220, 220, 220];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.row.raw && data.row.raw[0] === 'GRAND TOTAL') {
            data.cell.styles.fillColor = [128, 128, 128];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { top: currentY }
      });

      // Save the PDF
      const fileName = `${(reportInfo.report_label || reportInfo.name).replace(/[^a-z0-9]/gi, '_')}_with_charts_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      console.log('PDF with charts generated successfully');

    } catch (error) {
      console.error('Error generating PDF with charts:', error);
      alert('Error generating PDF with charts. Please try again.');
    }
  };

  const exportToExcel = () => {
    try {
      console.log('Exporting to Excel...');
      console.log('previewData length:', previewData ? previewData.length : 0);
      console.log('selectedFields length:', selectedFields ? selectedFields.length : 0);
      console.log('selectedFields:', selectedFields);
      console.log('previewData sample:', previewData ? previewData.slice(0, 2) : 'No data');
      console.log('reportInfo:', reportInfo);

      // Prepare worksheet data - use the same data structure as displayed in the table
      const worksheetData = [];

      // Add headers - use the same logic as the table headers
      const headers = [];

      // If there's grouping, show grouped fields first, then non-grouped fields
      if (grouping && grouping.hasGrouping && grouping.groupedFields) {
        // Add grouped field headers
        grouping.groupedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });

        // Add non-grouped field headers
        selectedFields.filter(field =>
          !grouping.groupedFields.some(groupField =>
            groupField.field_name === field.field_name &&
            groupField.object_name === field.object_name
          )
        ).forEach(field => {
          headers.push(field.field_label || field.field_name);
        });
      } else {
        // No grouping, show all fields normally
        selectedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });
      }

      worksheetData.push(headers);

      // Add data rows - use the same data structure as displayed
      if (previewData && previewData.length > 0) {
        previewData.forEach(row => {
          const rowData = [];

          if (grouping && grouping.hasGrouping && grouping.groupedFields) {
            // Add grouped field values
            grouping.groupedFields.forEach(field => {
              const value = getFieldValueForExport(field, row);
              rowData.push(formatValueForPDF(value, field.data_type));
            });

            // Add non-grouped field values
            selectedFields.filter(field =>
              !grouping.groupedFields.some(groupField =>
                groupField.field_name === field.field_name &&
                groupField.object_name === field.object_name
              )
            ).forEach(field => {
              const value = getFieldValueForExport(field, row);
              rowData.push(formatValueForPDF(value, field.data_type));
            });
          } else {
            // No grouping, add all field values
            selectedFields.forEach(field => {
              const value = getFieldValueForExport(field, row);
              rowData.push(formatValueForPDF(value, field.data_type));
            });
          }

          worksheetData.push(rowData);
        });
      }

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();

      // Prepare complete data starting with metadata, then headers, then data
      const reportTitle = reportInfo.report_label || reportInfo.name;
      const objectInfo = reportInfo.report_objects && reportInfo.report_objects.length > 0
        ? reportInfo.report_objects.map(obj => obj.object_label || obj.object_name).join(' & ')
        : reportInfo.object_label || reportInfo.object_name;

      // Create complete data array: metadata + headers + data
      const completeData = [
        [`Report: ${reportTitle || reportInfo?.name || 'N/A'}`],
        [`Objects: ${objectInfo || reportInfo?.object_name || 'N/A'}`],
        [`Generated on: ${new Date().toLocaleDateString()}`],
        [`Total Records: ${previewData ? previewData.length : 0}`],
        [''], // Empty row separator
        ...worksheetData // This includes headers + data
      ];

      // Create worksheet from complete data
      const worksheet = XLSX.utils.aoa_to_sheet(completeData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report Data');

      // Generate and download file
      const fileName = `${(reportInfo.report_label || reportInfo.name).replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error generating Excel file. Please try again.');
    }
  };

  const exportToFormattedExcel = () => {
    try {
      console.log('Exporting to Formatted Excel...');
      console.log('previewData length:', previewData ? previewData.length : 0);
      console.log('selectedFields length:', selectedFields ? selectedFields.length : 0);
      console.log('grouping:', grouping);

      const groupingFields = grouping.groupedFields;
      console.log('groupingFields:', groupingFields);
      // Helper function to get field value from row
      const getFieldValue = (field, row) => {
        const tableName = field.object_name || 'main';
        const aliasedFieldName = `${tableName}_${field.field_name}`;
        const fieldData = row[aliasedFieldName] || row[field.field_name];

        if (fieldData) {
          if (typeof fieldData === 'object' && fieldData !== null) {
            return fieldData.displayValue || fieldData.value || 'N/A';
          } else {
            return fieldData;
          }
        }
        return 'N/A';
      };

      // Simple groupByFields function
      function groupByFields(data, groupFields) {
        return data.reduce((acc, row) => {
          let currentLevel = acc;
          groupFields.forEach((field, index) => {
            const fieldValue = getFieldValue(field, row);

            // if value not initialized, create new object/array
            if (!currentLevel[fieldValue]) {
              currentLevel[fieldValue] = (index === groupFields.length - 1) ? [] : {};
            }

            // go deeper
            if (index === groupFields.length - 1) {
              currentLevel[fieldValue].push(row);
            } else {
              currentLevel = currentLevel[fieldValue];
            }
          });

          return acc;
        }, {});
      }

      const nestedGroups = groupByFields(previewData, groupingFields);

      console.log('nestedGroups:', nestedGroups);
      const printedGroups = {};
      const calculatedGroup = {};

      const initializeGroups = (groups, fieldIndex = 0, parentPath = '') => {
        for (const key in groups) {
          const value = groups[key];
          const field = groupingFields[fieldIndex];
          const currentPath = parentPath ? `${parentPath}|${key}` : key;
          const compositeKey = `${field.field_name}_${currentPath}`;

          if (Array.isArray(value)) {
            printedGroups[compositeKey] = false;
            calculatedGroup[compositeKey] = 0;
          } else if (typeof value === 'object' && value !== null) {
            printedGroups[compositeKey] = false;
            calculatedGroup[compositeKey] = 0;
            initializeGroups(value, fieldIndex + 1, currentPath);
          }
        }
      };

      initializeGroups(nestedGroups);

      // Helper function to find the hierarchical path for a row in nestedGroups
      const findRowPath = (row, groups, fieldIndex = 0, currentPath = '') => {
        const field = groupingFields[fieldIndex];
        const fieldValue = getFieldValue(field, row);

        if (!fieldValue || !groups[fieldValue]) {
          return null;
        }

        const newPath = currentPath ? `${currentPath}|${fieldValue}` : fieldValue;

        if (fieldIndex === groupingFields.length - 1) {
          const rows = groups[fieldValue];
          if (Array.isArray(rows) && rows.includes(row)) {
            return newPath;
          }
        } else {
          return findRowPath(row, groups[fieldValue], fieldIndex + 1, newPath);
        }

        return null;
      };

      // Fill the counts using hierarchical composite keys
      previewData.forEach((row) => {
        const rowPath = findRowPath(row, nestedGroups);
        if (rowPath) {
          groupingFields.forEach((field, fieldIndex) => {
            const fieldValue = getFieldValue(field, row);
            if (fieldValue) {
              const pathParts = rowPath.split('|');
              const currentPath = pathParts.slice(0, fieldIndex + 1).join('|');
              const compositeKey = `${field.field_name}_${currentPath}`;

              if (calculatedGroup.hasOwnProperty(compositeKey)) {
                calculatedGroup[compositeKey]++;
              }
            }
          });
        }
      });
      console.log('## calculatedGroup', calculatedGroup);

      // Prepare worksheet data - use the same data structure as displayed in the table with grouping
      const worksheetData = [];

      // Add headers - use the same logic as the table headers
      const headers = [];

      // If there's grouping, show grouped fields first, then non-grouped fields
      if (grouping && grouping.hasGrouping && grouping.groupedFields) {
        // Add grouped field headers
        grouping.groupedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });

        // Add non-grouped field headers
        selectedFields.filter(field =>
          !grouping.groupedFields.some(groupField =>
            groupField.field_name === field.field_name &&
            groupField.object_name === field.object_name
          )
        ).forEach(field => {
          headers.push(field.field_label || field.field_name);
        });
      } else {
        // No grouping, show all fields normally
        selectedFields.forEach(field => {
          headers.push(field.field_label || field.field_name);
        });
      }

      worksheetData.push(headers);

      // Add data rows with subtotals and grand totals if there's grouping
      if (grouping && grouping.hasGrouping && previewData && previewData.length > 0) {
        // Create grouped data structure manually (similar to renderGroupedData but for export)
        const groupedData = {};
        const groupedFields = grouping.groupedFields;

        // Group the data
        previewData.forEach(row => {
          let currentGroup = groupedData;

          // Navigate/create group hierarchy
          groupedFields.forEach((field, fieldIndex) => {
            const fieldValue = getFieldValueForExport(field, row);

            if (!currentGroup[fieldValue]) {
              currentGroup[fieldValue] = fieldIndex === groupedFields.length - 1 ? [] : {};
            }

            if (fieldIndex === groupedFields.length - 1) {
              currentGroup[fieldValue].push(row);
            } else {
              currentGroup = currentGroup[fieldValue];
            }
          });
        });

        // Helper function to recursively process grouped data with subtotals (matching ReportPreview logic)
        const processGroupedDataWithSubtotals = (groups, level = 0, parentKey = '') => {
          Object.keys(groups).forEach(groupKey => {
            const groupValue = groups[groupKey];

            if (Array.isArray(groupValue)) {
              // Last level - process data rows
              groupValue.forEach(row => {
                const rowData = [];

                // Add grouped field values
                groupedFields.forEach(field => {
                  const value = getFieldValueForExport(field, row);
                  rowData.push(formatValueForPDF(value, field.data_type));
                });

                // Add non-grouped field values
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  const value = getFieldValueForExport(field, row);
                  rowData.push(formatValueForPDF(value, field.data_type));
                });

                worksheetData.push(rowData);
              });

              // Add subtotal for this group (last level)
              if (Object.keys(summarizedFields).length > 0) {
                const subtotalRow = [];

                // Add empty cells for grouped fields, show 'Subtotal' at current level
                groupedFields.forEach((field, fieldIndex) => {
                  if (fieldIndex === level) {
                    // Use calculatedGroup count like in UI display
                    let storedCount = 0;
                    if (Array.isArray(groupValue) && groupValue.length > 0) {
                      const firstRow = groupValue[0];
                      const currentValue = getFieldValue(field, firstRow);
                      const rowPath = findRowPath(firstRow, nestedGroups);

                      // Create composite key matching the structure in calculatedGroup
                      let compositeKey;
                      if (level === 0) {
                        // First level: field_name + value
                        compositeKey = `${field.field_name}_${currentValue}`;
                      } else {
                        // Subsequent levels: field_name + path
                        const pathParts = rowPath.split('|');
                        const currentPath = pathParts.slice(0, level + 1).join('|');
                        compositeKey = `${field.field_name}_${currentPath}`;
                      }

                      storedCount = calculatedGroup[compositeKey] || 0;
                      console.log('Excel last level subtotal debug:', { level, currentValue, rowPath, compositeKey, storedCount });
                    }
                    console.log('## Excel count', `Subtotal (${storedCount})`);
                    subtotalRow.push(`Subtotal (${storedCount})`);
                  } else {
                    subtotalRow.push('');
                  }
                });

                // Add subtotals for summarized fields
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  if (summarizedFields[field.id]) {
                    const aggregation = summarizedFields[field.id].aggregation;
                    let subtotalValue = '';

                    switch (aggregation) {
                      case 'sum':
                        subtotalValue = groupValue.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        break;
                      case 'avg':
                        const sum = groupValue.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        subtotalValue = groupValue.length > 0 ? sum / groupValue.length : 0;
                        break;
                      case 'count':
                        subtotalValue = groupValue.length;
                        break;
                      case 'min':
                        subtotalValue = Math.min(...groupValue.map(row => getAggExportNum(field, row)));
                        break;
                      case 'max':
                        subtotalValue = Math.max(...groupValue.map(row => getAggExportNum(field, row)));
                        break;
                    }

                    subtotalRow.push(formatValueForPDF(subtotalValue, field.data_type));
                  } else {
                    subtotalRow.push('');
                  }
                });

                worksheetData.push(subtotalRow);
              }
            } else {
              // Not last level - recursively process sub-groups
              processGroupedDataWithSubtotals(groupValue, level + 1, parentKey ? `${parentKey}|${groupKey}` : groupKey);

              // Add subtotal for this group level (intermediate level)
              if (Object.keys(summarizedFields).length > 0) {
                const subtotalRow = [];

                // Add empty cells for grouped fields, show 'Subtotal' at current level
                groupedFields.forEach((field, fieldIndex) => {
                  if (fieldIndex === level) {
                    // Use calculatedGroup count like in UI display
                    let storedCount = 0;
                    if (groupValue && (Array.isArray(groupValue) ? groupValue.length > 0 : Object.keys(groupValue).length > 0)) {
                      let currentValue, rowPath;

                      if (Array.isArray(groupValue)) {
                        // Last level - value is an array of records
                        const firstRow = groupValue[0];
                        currentValue = getFieldValue(field, firstRow);
                        rowPath = findRowPath(firstRow, nestedGroups);
                      } else {
                        // Intermediate level - value is an object with nested groups
                        currentValue = groupKey; // Use the key directly since it represents the group value

                        // Find a sample row from the nested data to get the path
                        const sampleRow = Object.values(groupValue)[0]?.[0]; // Get first record from first nested group
                        if (sampleRow) {
                          rowPath = findRowPath(sampleRow, nestedGroups);
                        }
                      }

                      // Create composite key matching the structure in calculatedGroup
                      let compositeKey;
                      if (level === 0) {
                        // First level: field_name + value
                        compositeKey = `${field.field_name}_${currentValue}`;
                      } else {
                        // Subsequent levels: field_name + path
                        const pathParts = rowPath.split('|');
                        const currentPath = pathParts.slice(0, level + 1).join('|');
                        compositeKey = `${field.field_name}_${currentPath}`;
                      }

                      storedCount = calculatedGroup[compositeKey] || 0;
                      console.log('Excel intermediate subtotal debug:', { level, currentValue, rowPath, compositeKey, storedCount });
                    }

                    subtotalRow.push(`Subtotal (${storedCount})`);
                  } else {
                    subtotalRow.push('');
                  }
                });

                // Calculate subtotal for this group - get all rows from nested structure
                const getAllRows = (obj) => {
                  let allRows = [];
                  for (const subKey in obj) {
                    const subValue = obj[subKey];
                    if (Array.isArray(subValue)) {
                      allRows = allRows.concat(subValue);
                    } else if (typeof subValue === 'object' && subValue !== null) {
                      allRows = allRows.concat(getAllRows(subValue));
                    }
                  }
                  return allRows;
                };

                const groupData = getAllRows(groupValue);

                // Add subtotals for summarized fields
                selectedFields.filter(field =>
                  !groupedFields.some(groupField =>
                    groupField.field_name === field.field_name &&
                    groupField.object_name === field.object_name
                  )
                ).forEach(field => {
                  if (summarizedFields[field.id]) {
                    const aggregation = summarizedFields[field.id].aggregation;
                    let subtotalValue = '';

                    switch (aggregation) {
                      case 'sum':
                        subtotalValue = groupData.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        break;
                      case 'avg':
                        const sum = groupData.reduce((sum, row) => {
                          const val = getAggExportNum(field, row);
                          return sum + val;
                        }, 0);
                        subtotalValue = groupData.length > 0 ? sum / groupData.length : 0;
                        break;
                      case 'count':
                        subtotalValue = groupData.length;
                        break;
                      case 'min':
                        subtotalValue = Math.min(...groupData.map(row => getAggExportNum(field, row)));
                        break;
                      case 'max':
                        subtotalValue = Math.max(...groupData.map(row => getAggExportNum(field, row)));
                        break;
                    }

                    subtotalRow.push(formatValueForPDF(subtotalValue, field.data_type));
                  } else {
                    subtotalRow.push('');
                  }
                });

                worksheetData.push(subtotalRow);
              }
            }
          });
        };

        processGroupedDataWithSubtotals(groupedData);

        // Add grand total row
        if (previewData.length > 0) {
          const grandTotalRow = [];

          // Add grand total label
          grandTotalRow.push('Grand Total');

          // Add empty cells for other grouped fields
          for (let i = 1; i < groupedFields.length; i++) {
            grandTotalRow.push('');
          }

          // Add grand totals for summarized fields
          selectedFields.filter(field =>
            !groupedFields.some(groupField =>
              groupField.field_name === field.field_name &&
              groupField.object_name === field.object_name
            )
          ).forEach(field => {
            if (summarizedFields[field.id] && summarizedFields[field.id].aggregation) {
              const values = previewData.map(row => getAggExportNum(field, row));

              let aggregatedValue = 0;
              switch (summarizedFields[field.id].aggregation) {
                case 'sum':
                  aggregatedValue = values.reduce((sum, val) => sum + val, 0);
                  break;
                case 'avg':
                  aggregatedValue = values.reduce((sum, val) => sum + val, 0) / values.length;
                  break;
                case 'count':
                  aggregatedValue = values.length;
                  break;
                case 'max':
                  aggregatedValue = Math.max(...values);
                  break;
                case 'min':
                  aggregatedValue = Math.min(...values);
                  break;
              }

              grandTotalRow.push(formatValue(aggregatedValue, field.data_type, field.display_config || field.displayConfig));
            } else {
              grandTotalRow.push('');
            }
          });

          worksheetData.push(grandTotalRow);
        }

      } else {
        // No grouping, add regular data rows
        previewData.forEach(row => {
          const rowData = [];
          selectedFields.forEach(field => {
            const value = getFieldValueForExport(field, row);
            rowData.push(formatValueForPDF(value, field.data_type));
          });
          worksheetData.push(rowData);
        });
      }

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();

      // Prepare complete data starting with metadata, then headers, then data
      const reportTitle = reportInfo.report_label || reportInfo.name;
      const objectInfo = reportInfo.report_objects && reportInfo.report_objects.length > 0
        ? reportInfo.report_objects.map(obj => obj.object_label || obj.object_name).join(' & ')
        : reportInfo.object_label || reportInfo.object_name;

      // Create complete data array: metadata + headers + data with subtotals/grand totals
      const completeData = [
        [`Report: ${reportTitle || reportInfo?.name || 'N/A'}`],
        [`Objects: ${objectInfo || reportInfo?.object_name || 'N/A'}`],
        [`Generated on: ${new Date().toLocaleDateString()}`],
        [`Total Records: ${previewData ? previewData.length : 0}`],
        [''], // Empty row separator
        ...worksheetData // This includes headers + data with subtotals/grand totals
      ];

      // Create worksheet from complete data
      const worksheet = XLSX.utils.aoa_to_sheet(completeData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Formatted Report Data');

      // Generate and download file
      const fileName = `${(reportInfo.report_label || reportInfo.name).replace(/[^a-z0-9]/gi, '_')}_formatted_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

    } catch (error) {
      console.error('Error generating Formatted Excel:', error);
      alert('Error generating Formatted Excel file. Please try again.');
    }
  };

  const exportToCSV = () => {
    try {
      console.log('Exporting to CSV...');
      console.log('previewData length:', previewData ? previewData.length : 0);
      console.log('selectedFields length:', selectedFields ? selectedFields.length : 0);
      console.log('selectedFields:', selectedFields);

      if (!previewData || previewData.length === 0) {
        alert('No data available to export');
        return;
      }

      if (!selectedFields || selectedFields.length === 0) {
        alert('No fields selected for export');
        return;
      }

      // Create CSV headers from selected fields
      const headers = selectedFields.map(field => field.field_label || field.field_name);

      // Create CSV rows from preview data
      const csvRows = previewData.map(row => {
        return selectedFields.map(field => {
          const value = row[field.field_name];
          const formattedValue = formatValue(value, field.data_type, field.display_config || field.displayConfig);
          // Escape CSV values that contain commas, quotes, or newlines
          if (typeof formattedValue === 'string' && (formattedValue.includes(',') || formattedValue.includes('"') || formattedValue.includes('\n'))) {
            return `"${formattedValue.replace(/"/g, '""')}"`;
          }
          return formattedValue;
        });
      });

      // Combine headers and rows
      const csvContent = [headers, ...csvRows]
        .map(row => row.join(','))
        .join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `report_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('CSV export completed successfully');
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV file. Please try again.');
    }
  };


  const handleExport = (format) => {
    console.log('handleExport called with format:', format);
    if (format === 'pdf') {
      console.log('Calling exportToPDF...');
      exportToPDF();
    } else if (format === 'excel') {
      console.log('Calling exportToExcel...');
      exportToExcel();
    } else if (format === 'formatted-excel') {
      console.log('Calling exportToFormattedExcel...');
      exportToFormattedExcel();
    } else if (format === 'csv') {
      console.log('Calling exportToCSV...');
      exportToCSV();
    }
  };


  // Create summarized data for charts using available data
  const createSummarizedDataForCharts = () => {
    if (!groupingInfo || !groupingInfo.hasGrouping || !previewData || previewData.length === 0) {
      return {};
    }

    const chartAggregationFormatter = makeReportFormatAggregatedDisplay(reportConversionBaseCode);

    const summarizedData = {};
    const groupedFields = groupingInfo.groupedFields;
    const nonGroupedFields = selectedFields.filter(field =>
      !groupedFields.some(groupField =>
        groupField.field_name === field.field_name &&
        groupField.object_name === field.object_name
      )
    );

    // Initialize metadata
    summarizedData._metadata = {
      group_fields: {},
      summarized_fields: {},
      report_info: {
        total_records: previewData.length,
        group_count: groupedFields.length,
        summarized_field_count: Object.keys(summarizedFields).length,
        extraction_timestamp: new Date().toISOString(),
        data_structure_version: '2.0'
      }
    };

    // Add metadata for grouped fields
    groupedFields.forEach(field => {
      summarizedData._metadata.group_fields[field.field_name] = {
        field_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        object_name: field.object_name,
        field_type: field.field_type
      };
    });

    // Add metadata for summarized fields
    nonGroupedFields.forEach(field => {
      const summarizedField = summarizedFields[field.id];
      if (summarizedField) {
        summarizedData._metadata.summarized_fields[field.field_name] = {
          field_id: field.id,
          field_name: field.field_name,
          field_label: field.field_label,
          data_type: field.data_type,
          object_name: field.object_name,
          aggregation: summarizedField.aggregation
        };
      }
    });

    // Process all grouped fields, not just the first one
    if (groupedFields.length > 0) {
      // Process each grouped field to create data structures for all possible xAxisFields
      groupedFields.forEach(primaryGroupField => {
        const groupFieldName = primaryGroupField.field_name;

        // Get unique group values for this specific field
        const groupValues = new Set();
        previewData.forEach(row => {
          const tableName = primaryGroupField.object_name || 'main';
          const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
          const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

          let groupValue;
          if (typeof fieldData === 'object' && fieldData !== null) {
            groupValue = fieldData.value || fieldData.displayValue;
          } else {
            groupValue = fieldData;
          }

          // Convert null, undefined, or empty string to 'N/A' for chart grouping
          if (groupValue === null || groupValue === undefined || groupValue === '') {
            groupValue = 'N/A';
          }

          groupValues.add(groupValue);
        });

        // Initialize each summarized field in the root level (only once)
        if (groupFieldName === groupedFields[0].field_name) {
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField) {
              if (!summarizedData[field.field_name]) {
                summarizedData[field.field_name] = {};
              }
            }
          });
        }

        // Calculate aggregated values for each group and field
        groupValues.forEach(groupValue => {
          // Filter data for this group
          const groupData = previewData.filter(row => {
            const tableName = primaryGroupField.object_name || 'main';
            const aliasedFieldName = `${tableName}_${primaryGroupField.field_name}`;
            const fieldData = row[aliasedFieldName] || row[primaryGroupField.field_name];

            let rowGroupValue;
            if (typeof fieldData === 'object' && fieldData !== null) {
              rowGroupValue = fieldData.value || fieldData.displayValue;
            } else {
              rowGroupValue = fieldData;
            }

            // Convert null, undefined, or empty string to 'N/A' for comparison
            if (rowGroupValue === null || rowGroupValue === undefined || rowGroupValue === '') {
              rowGroupValue = 'N/A';
            }

            return rowGroupValue === groupValue;
          });

          // Calculate aggregations for each summarized field
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField) {
              const aggregatedValue = calculateReportAggregation(
                groupData,
                field,
                summarizedField.aggregation,
                reportConversionBaseCode,
                chartAggregationFormatter,
                reportConversionLookup
              );

              const n = getAggregationNumericResult(
                groupData,
                field,
                summarizedField.aggregation,
                reportConversionBaseCode,
                reportConversionLookup
              );

              let chartValue = aggregatedValue;
              if (n != null) {
                chartValue = n;
              } else if (field.data_type === 'currency' && typeof aggregatedValue === 'string') {
                const numericValue = parseFloat(
                  String(aggregatedValue).replace(/[^0-9.-]/g, '')
                );
                chartValue = isNaN(numericValue) ? 0 : numericValue;
              } else if (field.data_type === 'number' || field.data_type === 'decimal') {
                const numericValue = parseFloat(aggregatedValue);
                chartValue = isNaN(numericValue) ? 0 : numericValue;
              }

              summarizedData[field.field_name][groupValue] = chartValue;
            }
          });
        });

        // Create the main structure that ChartDisplay expects for this group field
        summarizedData[groupFieldName] = {};
        groupValues.forEach(groupValue => {
          summarizedData[groupFieldName][groupValue] = {};
          nonGroupedFields.forEach(field => {
            const summarizedField = summarizedFields[field.id];
            if (summarizedField && summarizedData[field.field_name][groupValue] !== undefined) {
              // ChartDisplay expects: { "salary": { "value": 35000 } }
              summarizedData[groupFieldName][groupValue][field.field_name] = {
                value: summarizedData[field.field_name][groupValue]
              };
            }
          });
        });
      });
    }

    return summarizedData;
  };

  // Get summarized data for charts
  const summarizedData = createSummarizedDataForCharts() || {};

  const formatReportTableCell = (fieldData, field, row) => {
    const money = formatReportCurrencyCell(
      fieldData,
      field,
      row,
      reportConversionBaseCode,
      resolveCurrency('INR'),
      reportConversionLookup
    );
    if (money != null) return money;
    return formatValue(
      fieldData,
      field.data_type,
      field.display_config || field.displayConfig
    );
  };

  return (
    <div className="report-data-viewer" style={{ position: 'relative' }}>
      {showFilters && (
        <div className="filter-modal-overlay">
          <div className="filter-modal">
            <div className="filter-modal-header">
              <h3><FaFilter /> Manage Report Filters</h3>
              <button
                type="button"
                onClick={() => onFilterToggle && onFilterToggle()}
                className="close-modal-btn"
              >
                <FaTimes />
              </button>
            </div>

            {/* Filter Logic Display */}
            {reportInfo && (reportInfo.filter_logic || (reportFilters && reportFilters.length > 1)) && (
              <div className="filter-logic-display">
                <strong>Filter Logic:</strong> {reportInfo.filter_logic || 'AND'}
                {reportInfo.filter_custom_logic && (
                  <div className="custom-filter-logic">
                    <strong>Custom Logic:</strong> {reportInfo.filter_custom_logic}
                  </div>
                )}
              </div>
            )}

            <div className="filter-modal-content">
              <div className="filters-table-container">
                <table className="filters-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Field</th>
                      <th>Operator</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportFilters && reportFilters.length > 0 ? reportFilters.map((filter, index) => (
                      <tr key={filter.id || index} className="filter-row">
                        <td className="filter-number">
                          {index + 1}
                        </td>

                        <td>
                          <span className="filter-field-name">
                            {filter.field_label || filter.field_name}
                            {filter.is_required && <span className="required">*</span>}
                            {filter.is_editable === false && <span className="readonly-badge">(Read Only)</span>}
                          </span>
                          <small className="filter-field-type">({filter.data_type})</small>
                        </td>

                        <td>
                          <select
                            value={appliedFilters[`${filter.id}_operator`] !== undefined ? appliedFilters[`${filter.id}_operator`] : filter.operator}
                            onChange={(e) => handleFilterChange(`${filter.id}_operator`, e.target.value)}
                            className="filter-operator-select"
                            disabled={filter.is_editable === false}
                            readOnly={filter.is_editable === false}
                          >
                            {['text', 'email'].includes(filter.data_type) && (
                              <>
                                <option value="equals">Equals</option>
                                <option value="not_equals">Not Equals</option>
                                <option value="contains">Contains</option>
                                <option value="starts_with">Starts With</option>
                                <option value="ends_with">Ends With</option>
                              </>
                            )}
                            {['number', 'decimal', 'currency', 'date', 'datetime'].includes(filter.data_type) && (
                              <>
                                <option value="equals">Equals</option>
                                <option value="not_equals">Not Equals</option>
                                <option value="greater_than">Greater Than</option>
                                <option value="less_than">Less Than</option>
                                <option value="greater_than_or_equal">Greater Than or Equal</option>
                                <option value="less_than_or_equal">Less Than or Equal</option>
                              </>
                            )}
                            {filter.data_type === 'boolean' && (
                              <>
                                <option value="equals">Equals</option>
                                <option value="not_equals">Not Equals</option>
                              </>
                            )}
                            {filter.data_type === 'picklist' && (
                              <>
                                <option value="equals">Equals</option>
                                <option value="not_equals">Not Equals</option>
                                <option value="in">In</option>
                                <option value="not_in">Not In</option>
                              </>
                            )}
                          </select>
                        </td>

                        <td>
                          <div className="filter-value-container">
                            {filter.data_type === 'boolean' ? (
                              <select
                                value={appliedFilters[`${filter.id}_value`] !== undefined ? appliedFilters[`${filter.id}_value`] : filter.value}
                                onChange={(e) => handleFilterChange(`${filter.id}_value`, e.target.value)}
                                className="filter-value-select"
                                disabled={filter.is_editable === false}
                                readOnly={filter.is_editable === false}
                              >
                                <option value="">Select...</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                              </select>
                            ) : filter.data_type === 'picklist' && filter.picklist_values ? (
                              <select
                                value={appliedFilters[`${filter.id}_value`] !== undefined ? appliedFilters[`${filter.id}_value`] : filter.value}
                                onChange={(e) => handleFilterChange(`${filter.id}_value`, e.target.value)}
                                className="filter-value-select"
                                disabled={filter.is_editable === false}
                                readOnly={filter.is_editable === false}
                              >
                                <option value="">Select...</option>
                                {filter.picklist_values.map((value, idx) => (
                                  <option key={idx} value={value.value}>{value.label}</option>
                                ))}
                              </select>
                            ) : filter.data_type === 'date' ? (
                              <input
                                type="date"
                                value={appliedFilters[`${filter.id}_value`] !== undefined ? appliedFilters[`${filter.id}_value`] : filter.value}
                                onChange={(e) => handleFilterChange(`${filter.id}_value`, e.target.value)}
                                className="filter-value-input"
                                disabled={filter.is_editable === false}
                                readOnly={filter.is_editable === false}
                              />
                            ) : filter.data_type === 'datetime' ? (
                              <input
                                type="datetime-local"
                                value={appliedFilters[`${filter.id}_value`] !== undefined ? appliedFilters[`${filter.id}_value`] : filter.value}
                                onChange={(e) => handleFilterChange(`${filter.id}_value`, e.target.value)}
                                className="filter-value-input"
                                disabled={filter.is_editable === false}
                                readOnly={filter.is_editable === false}
                              />
                            ) : ['number', 'decimal', 'currency'].includes(filter.data_type) ? (
                              <input
                                type="number"
                                step={filter.data_type === 'decimal' ? '0.01' : '1'}
                                value={appliedFilters[`${filter.id}_value`] !== undefined ? appliedFilters[`${filter.id}_value`] : filter.value}
                                onChange={(e) => handleFilterChange(`${filter.id}_value`, e.target.value)}
                                className="filter-value-input"
                                placeholder="Enter value..."
                                disabled={filter.is_editable === false}
                                readOnly={filter.is_editable === false}
                              />
                            ) : (
                              <input
                                type="text"
                                value={appliedFilters[`${filter.id}_value`] !== undefined ? appliedFilters[`${filter.id}_value`] : filter.value}
                                onChange={(e) => handleFilterChange(`${filter.id}_value`, e.target.value)}
                                className="filter-value-input"
                                placeholder="Enter value..."
                                disabled={filter.is_editable === false}
                                readOnly={filter.is_editable === false}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          No editable filters available for this report.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="filter-modal-footer">
              <button
                type="button"
                onClick={() => onFilterToggle && onFilterToggle()}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="btn btn-outline-secondary"
              >
                Reset Filters
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="btn btn-primary"
              >
                <FaCheck /> Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}



      {dataLoading ? (
        <div className="data-loading">
          <div className="spinner"></div>
          <p>Loading report data...</p>
        </div>
      ) : (
        <div className="report-data-display">
          {/* Charts Section - positioned within the data table section for proper scrolling */}
          {(() => {
            console.log('ReportDataViewer - Charts rendering check:', {
              chartsLength: charts ? charts.length : 'undefined',
              charts: charts,
              summarizedDataKeys: Object.keys(summarizedData),
              summarizedData: summarizedData,
              hasGrouping: groupingInfo?.hasGrouping,
              previewDataLength: previewData?.length
            });
            return null;
          })()}
          {charts && charts.length > 0 && showChartsSection ? (
            <div className="charts-section">
              <div className="charts-header">
                <h3>Report Charts</h3>
                <button
                  className="charts-toggle-btn"
                  onClick={toggleChartsSection}
                  title="Hide Charts"
                >
                  <FaMinus />
                </button>
              </div>
              <div className={`charts-grid ${charts.length === 1 ? 'single-chart' :
                charts.length >= 3 ? 'multi-chart' :
                  ''
                }`}>
                {charts
                  .sort((a, b) => (a.sortOrder || 1) - (b.sortOrder || 1)) // Sort charts by sortOrder
                  .map((chart) => {
                    console.log('ReportDataViewer - Rendering ChartDisplay for chart:', {
                      chartId: chart.id,
                      chartTitle: chart.title,
                      chartType: chart.chartType,
                      xAxisField: chart.xAxisField,
                      yAxisFields: chart.yAxisFields,
                      selectedFieldsLength: selectedFields.length,
                      summarizedFieldsKeys: Object.keys(summarizedFields),
                      selectedGroupsLength: selectedGroups.length,
                      summarizedDataKeys: Object.keys(summarizedData),
                      summarizedDataSample: summarizedData
                    });
                    return (
                      <div
                        key={chart.id}
                        data-chart-id={chart.id}
                        className={`chart-grid-item ${
                          charts.length === 1 || chart.columnSpan === 2
                            ? 'full-width'
                            : 'half-width'
                        }`}
                      >
                        <ChartDisplay
                          chart={chart}
                          selectedFields={selectedFields}
                          summarizedFields={summarizedFields}
                          selectedGroups={selectedGroups}
                          summarizedData={summarizedData}
                          previewData={previewData}
                          isPreview={true}
                          onView={handleViewChart}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : charts && charts.length > 0 && !showChartsSection ? (
            <div className="charts-toggle-bar">
              <button
                className="charts-show-btn"
                onClick={toggleChartsSection}
                title="Show Charts"
              >
                <FaChartBar />
                <span>Show Charts ({charts.length})</span>
              </button>
            </div>
          ) : null}

          {previewData.length > 0 && (
            <div className="report-runtime-sort-bar">
              <span className="report-runtime-sort-label">On-screen sort (not saved to report):</span>
              <select
                className="form-select form-select-sm report-runtime-sort-select"
                value={runtimeSort?.fieldId ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  if (!v) setRuntimeSort(null);
                  else
                    setRuntimeSort(s =>
                      s && String(s.fieldId) === v ? s : { fieldId: v, direction: 'asc' }
                    );
                }}
                aria-label="Sort by column"
              >
                <option value="">Report order</option>
                {viewerColumnFields.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.field_label}
                  </option>
                ))}
              </select>
              <select
                className="form-select form-select-sm report-runtime-sort-select"
                value={runtimeSort?.direction ?? 'asc'}
                disabled={!runtimeSort}
                onChange={e =>
                  setRuntimeSort(s =>
                    s ? { ...s, direction: e.target.value } : null
                  )
                }
                aria-label="Sort direction"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={!runtimeSort}
                onClick={() => setRuntimeSort(null)}
              >
                Reset
              </button>
            </div>
          )}

          <table className="report-table">
            <thead>
              <tr className="report-headers">
                {groupingInfo && groupingInfo.hasGrouping ? (
                  // Show all grouped fields first, then non-grouped fields
                  <>
                    {groupingInfo.groupedFields.map(field => (
                      <th
                        key={field.id}
                        className="header-cell grouped-header report-header-sortable"
                        onClick={() => cycleRuntimeSortForField(field.id)}
                        title="Click to sort (on-screen only; does not change saved report)"
                      >
                        {field.field_label}
                        {summarizedFields[field.id] && (
                          <span className="aggregation-indicator">
                            ({summarizedFields[field.id].aggregation})
                          </span>
                        )}
                        {runtimeSortIndicator(field.id) ? (
                          <span className="report-runtime-sort-mark"> {runtimeSortIndicator(field.id)}</span>
                        ) : null}
                      </th>
                    ))}
                    {selectedFields.filter(field =>
                      !groupingInfo.groupedFields.some(groupField =>
                        groupField.field_name === field.field_name &&
                        groupField.object_name === field.object_name
                      )
                    ).map(field => (
                      <th
                        key={field.id}
                        className="header-cell report-header-sortable"
                        onClick={() => cycleRuntimeSortForField(field.id)}
                        title="Click to sort (on-screen only; does not change saved report)"
                      >
                        {field.field_label}
                        {summarizedFields[field.id] && (
                          <span className="aggregation-indicator">
                            ({summarizedFields[field.id].aggregation})
                          </span>
                        )}
                        {runtimeSortIndicator(field.id) ? (
                          <span className="report-runtime-sort-mark"> {runtimeSortIndicator(field.id)}</span>
                        ) : null}
                      </th>
                    ))}
                  </>
                ) : (
                  // Show all fields normally
                  selectedFields.map(field => (
                    <th
                      key={field.id}
                      className="header-cell report-header-sortable"
                      onClick={() => cycleRuntimeSortForField(field.id)}
                      title="Click to sort (on-screen only; does not change saved report)"
                    >
                      {field.field_label}
                      {summarizedFields[field.id] && (
                        <span className="aggregation-indicator">
                          ({summarizedFields[field.id].aggregation})
                        </span>
                      )}
                      {runtimeSortIndicator(field.id) ? (
                        <span className="report-runtime-sort-mark"> {runtimeSortIndicator(field.id)}</span>
                      ) : null}
                    </th>
                  ))
                )}
              </tr>
            </thead>

            <tbody className="report-data">
              {previewData.length > 0 ? (
                groupingInfo && groupingInfo.hasGrouping ? (
                  // Render grouped data
                  renderGroupedData(
                    previewData,
                    selectedFields,
                    groupingInfo,
                    summarizedFields,
                    reportConversionBaseCode,
                    reportConversionLookup,
                    runtimeSort
                  )
                ) : (
                  <>
                    {/* Render regular data */}
                    {viewerFlatRows.map((row, index) => (
                      <tr key={index} className="data-row regular-row">
                        {selectedFields.map(field => {
                          // Use the aliased field name from the query
                          const tableName = field.object_name || 'main';
                          const aliasedFieldName = `${tableName}_${field.field_name}`;
                          const fieldData = row[aliasedFieldName] || row[field.field_name];
                          return (
                            <td
                              key={field.id}
                              className="data-cell"
                              style={{
                                textAlign: ['number', 'integer', 'decimal', 'currency', 'percent'].includes(field.data_type?.toLowerCase()) ? 'right' : 'left'
                              }}
                            >
                              {formatReportTableCell(fieldData, field, row)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* Grand total row for regular data */}
                    <tr key="grand-total" className="grand-total-row">
                      <td colSpan={selectedFields.length} className="grand-total-cell">
                        <span className="grand-total-label">Grand Total ({previewData.length})</span>
                      </td>
                    </tr>
                  </>
                )
              ) : (
                <tr>
                  <td colSpan={selectedFields.length} className="no-data-message">
                    <p>No records returned. Try adjusting filters or check the report configuration.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Chart Fullscreen Modal */}
      {showChartModal && selectedChart && (
        <div className="chart-modal-overlay" onClick={handleCloseChartModal}>
          <div className="chart-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h2>{selectedChart.title}</h2>
              <button
                className="chart-modal-close"
                onClick={handleCloseChartModal}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="chart-modal-body">
              <ChartDisplay
                chart={selectedChart}
                selectedFields={selectedFields}
                summarizedFields={summarizedFields}
                selectedGroups={selectedGroups}
                summarizedData={summarizedData}
                previewData={previewData}
                isPreview={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ReportDataViewer;
